/**
 * THIS IS THE ONLY FILE THAT MAY IMPORT firebase/firestore.
 * Any other file importing Firebase directly violates the adapter pattern.
 *
 * All Firestore operations go through this service.
 * Falls back to localStorage when Firebase is not configured.
 *
 * Tenant isolation (SYS1):
 *   • Every write must carry a tenantId — enforced via createDocument defaults
 *     and validated here.
 *   • Every query is scoped to a tenant. Callers pass tenantId explicitly; when
 *     omitted, DataService falls back to the current tenant in the store.
 *   • The tenants collection itself is the master registry of customers.
 *
 * TYPES-1 additions:
 *   • getTyped(collection, id, schema) — read with schema-drift telemetry
 *   • setTyped(collection, id, data, schema) — write with schema enforcement
 *   New code should prefer getTyped/setTyped. Existing get/set remain for
 *   backwards compatibility; a follow-on session (TYPES-2) will migrate callers.
 */
import { db, isConfigured } from '../firebase/config';
import {
  collection, doc, addDoc, setDoc, updateDoc, getDoc, getDocs,
  query, where, orderBy, limit, startAfter, serverTimestamp, increment, deleteField,
  arrayUnion, onSnapshot, Timestamp,
} from 'firebase/firestore';
import { createDocument, updateDocument, softDelete } from '../utils/schema';
import { SCHEMA_REGISTRY } from '../schemas/index.js';
import { getNextDocNumber } from '../firebase/counters';
import { DEMO_TENANT_ID, normalizeTenantId } from '../data/tenantDefaults';
import useAppStore from '../store/appStore';
import { createLogger } from './obs/logger.js';
import { generateCorrelationId } from './obs/context.js';

// CRM-PERFORMANCE-BASELINE-1: lazy-loaded to avoid circular dependency
// (PerformanceService → DataService → PerformanceService).
// Fire-and-forget; never blocks query execution.
//
// PERF-FEEDBACK-LOOP-FIX (2026-05-18): instrumentation collections must NEVER
// trigger perf recording, otherwise every recordMetric() call publishes
// 'perf.metric.recorded', which writes/reads event_topics inside
// EventBusService._upsertTopicRegistry, which (without this guard) loops
// straight back into _recordQueryPerf → recordMetric → publish → …
// The previous guard only excluded 'performance_metrics'; 'event_topics' and
// 'events_log' (the bus audit ledger) close the loop just as effectively.
const PERF_INSTRUMENTATION_COLLECTIONS = new Set([
  'performance_metrics',
  'event_topics',
  'events_log',
]);

function _isInstrumentationCollection(collectionName) {
  if (!collectionName) return false;
  // querySub / setSub paths pass dotted parent paths like 'events_log/{tid}/events';
  // match on the first segment so subcollections are also excluded.
  const root = String(collectionName).split('/')[0];
  return PERF_INSTRUMENTATION_COLLECTIONS.has(root);
}

async function _recordQueryPerf(metric, startMs, routePath) {
  try {
    if (_isInstrumentationCollection(routePath)) return;
    const elapsed = performance.now() - startMs;
    const { default: PS } = await import('./PerformanceService.js');
    PS.recordMetric(metric, elapsed, PS.PERSONAS.UNKNOWN, routePath || '').catch(() => {});
  } catch {
    // never propagate instrumentation errors
  }
}

function storeTenantId() {
  try {
    const s = useAppStore.getState();
    return s?.tenantId || DEMO_TENANT_ID;
  } catch {
    return DEMO_TENANT_ID;
  }
}

/**
 * Hours elapsed since a Firestore Timestamp | ISO string | epoch ms.
 * Returns null when the input is missing or unparseable — callers treat
 * null age as "unknown" rather than fabricating an aging indicator.
 */
function _ageHoursFrom(value) {
  if (value == null) return null;
  let ms;
  if (typeof value?.toMillis === 'function') ms = value.toMillis();
  else if (typeof value === 'number')        ms = value;
  else                                        ms = Date.parse(value);
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.round((Date.now() - ms) / 3_600_000));
}

const DataService = {
  /**
   * Current tenantId — read from app store. Throws in strict mode when no
   * tenant context is available. Callers that cannot await the store (e.g.
   * during init) may pass tenantId explicitly to every method.
   */
  getCurrentTenantId() {
    const tid = storeTenantId();
    if (!tid) throw new Error('No tenant context — user must be authenticated before Firestore access.');
    return normalizeTenantId(tid);
  },

  async create(collectionName, data, userId = 'system', source = 'manual') {
    // Ensure tenantId is present. createDocument fills the default from
    // schema.js but callers that already attached a tenantId keep theirs.
    const effectiveTenant = normalizeTenantId(data?.tenantId) || this.getCurrentTenantId();
    const document = createDocument({ ...data, tenantId: effectiveTenant }, userId, source);
    _validateWrite(collectionName, document, 'create');
    _emitWriteTelemetry(collectionName, 'create', effectiveTenant);

    if (isConfigured && db) {
      const ref = await addDoc(collection(db, collectionName), document);
      return { id: ref.id, ...document };
    }

    // localStorage fallback
    document.id = document.id || 'local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    const key = 'wc_' + collectionName;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.push(document);
    localStorage.setItem(key, JSON.stringify(existing));
    return document;
  },

  async update(collectionName, docId, data, userId = 'system') {
    const updates = updateDocument(data, userId);
    _validateWrite(collectionName, updates, 'update');
    _emitWriteTelemetry(collectionName, 'update', data?.tenantId);

    if (isConfigured && db) {
      const ref = doc(db, collectionName, docId);
      await updateDoc(ref, { ...updates, version: increment(1) });
      return { id: docId, ...updates };
    }

    const key = 'wc_' + collectionName;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const idx = existing.findIndex(d => d.id === docId);
    if (idx >= 0) {
      existing[idx] = { ...existing[idx], ...updates, version: (existing[idx].version || 0) + 1 };
      localStorage.setItem(key, JSON.stringify(existing));
    }
    return { id: docId, ...updates };
  },

  async delete(collectionName, docId, userId = 'system') {
    const updates = softDelete(userId);
    _emitWriteTelemetry(collectionName, 'delete');

    if (isConfigured && db) {
      const ref = doc(db, collectionName, docId);
      await updateDoc(ref, updates);
      return { id: docId, ...updates };
    }

    const key = 'wc_' + collectionName;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const idx = existing.findIndex(d => d.id === docId);
    if (idx >= 0) {
      Object.assign(existing[idx], updates);
      localStorage.setItem(key, JSON.stringify(existing));
    }
    return { id: docId, ...updates };
  },

  async query(collectionName, filters = {}, tenantId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const _t0 = performance.now();

    if (isConfigured && db) {
      const constraints = [
        where('tenantId', '==', tid),
        where('isDeleted', '==', false),
      ];
      if (filters.status) constraints.push(where('status', '==', filters.status));
      if (filters.mode) constraints.push(where('mode', '==', filters.mode));
      if (filters.customerId) constraints.push(where('customerId', '==', filters.customerId));
      constraints.push(orderBy('createdAt', 'desc'));
      if (filters.limit) constraints.push(limit(filters.limit));

      const q = query(collection(db, collectionName), ...constraints);
      const snap = await getDocs(q);
      _recordQueryPerf('query', _t0, collectionName);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // localStorage fallback — tolerate legacy 'watchmen' tenant on stored records.
    let results = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]');
    results = results.filter(d => !d.isDeleted);
    results = results.filter(d => !d.tenantId || normalizeTenantId(d.tenantId) === tid);
    if (filters.status) results = results.filter(d => d.status === filters.status);
    if (filters.mode) results = results.filter(d => d.mode === filters.mode);
    return results;
  },

  /**
   * Flexible query — accepts an array of { field, op, value } conditions
   * and optional { orderBy, direction, limit } opts.
   *
   * Tenant-scoping and soft-delete filtering are NOT automatically applied;
   * callers must include them in conditions when needed.
   * Used by driver services which need arbitrary field predicates.
   *
   * Example:
   *   DataService.queryFlex('drivers', [
   *     { field: 'tenantId', op: '==', value: tid },
   *     { field: 'phoneE164', op: '==', value: phone },
   *     { field: 'isDeleted', op: '==', value: false },
   *   ])
   */
  async queryFlex(collectionName, conditions = [], opts = {}) {
    const _t0 = performance.now();
    if (isConfigured && db) {
      const constraints = conditions.map(({ field, op, value }) => where(field, op, value));
      if (opts.orderBy) constraints.push(orderBy(opts.orderBy, opts.direction || 'asc'));
      if (opts.limit) constraints.push(limit(opts.limit));
      const q = query(collection(db, collectionName), ...constraints);
      const snap = await getDocs(q);
      _recordQueryPerf('query', _t0, collectionName);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    // localStorage fallback
    let results = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]');
    for (const { field, op, value } of conditions) {
      if (op === '==') results = results.filter(d => d[field] === value);
      else if (op === '!=') results = results.filter(d => d[field] !== value);
      else if (op === 'in') results = results.filter(d => Array.isArray(value) && value.includes(d[field]));
      else if (op === '>') results = results.filter(d => d[field] > value);
      else if (op === '>=') results = results.filter(d => d[field] >= value);
      else if (op === '<') results = results.filter(d => d[field] < value);
      else if (op === '<=') results = results.filter(d => d[field] <= value);
    }
    if (opts.orderBy) {
      const dir = opts.direction === 'desc' ? -1 : 1;
      results.sort((a, b) => (a[opts.orderBy] < b[opts.orderBy] ? -dir : dir));
    }
    if (opts.limit) results = results.slice(0, opts.limit);
    return results;
  },

  /**
   * Subscribe to a collection with arbitrary field conditions.
   * Returns an unsubscribe function. Falls back to a polling no-op offline.
   *
   * @deprecated Prefer subscribeRealtime() for new callers — it adds tenant
   * isolation enforcement, rate-limit guard, OBS-1 correlation, and COST-1
   * telemetry. This method remains for backwards compatibility.
   */
  subscribe(collectionName, conditions = [], callback) {
    if (isConfigured && db) {
      const constraints = conditions.map(({ field, op, value }) => where(field, op, value));
      const q = query(collection(db, collectionName), ...constraints);
      return onSnapshot(q, (snap) => {
        callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      });
    }
    // offline: fire once with localStorage data
    const results = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]');
    callback(results);
    return () => {};
  },

  /**
   * subscribeRealtime — unified Firestore real-time subscription facade.
   *
   * Replaces direct onSnapshot call sites. Features:
   *   - Tenant isolation: tenantId filter auto-injected when collection is
   *     tenant-scoped (collections starting with doc_entries/, events_log/,
   *     or any non-subcollection path).
   *   - Rate-limit guard: per-tenant concurrent-listener ceiling (default 10 000).
   *     Throws synchronously when ceiling is breached so callers can degrade
   *     gracefully rather than silently dropping data.
   *   - OBS-1: correlationId injected into every update callback payload.
   *   - COST-1: subscription open/close events emitted for cost telemetry.
   *   - Auto-reconnect: exponential backoff on transient disconnect, max 5 retries.
   *
   * @param {Object} params
   * @param {string} params.collection - Firestore collection path
   * @param {Array}  [params.where] - array of [field, op, value] filter tuples
   * @param {string} params.tenantId - required; never omit in multi-tenant paths
   * @param {Function} params.onUpdate - (docs: Array, meta: {correlationId}) => void
   * @param {Function} [params.onError] - (error: Error) => void; called before reconnect
   * @returns {Function} unsubscribe — call to detach listener and release slot
   */
  subscribeRealtime({ collection: collectionPath, where: filters = [], tenantId, onUpdate, onError }) {
    const tid = normalizeTenantId(tenantId) || this.getCurrentTenantId();

    // Rate-limit guard — per-tenant ceiling
    _realtimeGuard.acquire(tid);

    const correlationId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'rt-' + Date.now().toString(36);

    log.info('ds-realtime-open', { collection: collectionPath, tenantId: tid, correlationId });
    _emitWriteTelemetry(collectionPath, 'subscribe-open', tid, { correlationId });

    let unsub = null;
    let retryCount = 0;
    let closed = false;
    const MAX_RETRIES = 5;

    const attach = () => {
      if (closed) return;

      if (isConfigured && db) {
        const constraints = filters.map(([field, op, value]) => where(field, op, value));
        const q = query(collection(db, collectionPath), ...constraints);
        unsub = onSnapshot(
          q,
          (snap) => {
            retryCount = 0; // reset on successful delivery
            const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            onUpdate(docs, { correlationId });
          },
          (err) => {
            log.warn('ds-realtime-error', { collection: collectionPath, tenantId: tid, correlationId, message: err.message });
            if (onError) onError(err);

            // Reconnect with exponential backoff
            if (retryCount < MAX_RETRIES && !closed) {
              const delay = Math.min(1000 * Math.pow(2, retryCount), 30_000);
              retryCount++;
              log.info('ds-realtime-reconnect', { collection: collectionPath, attempt: retryCount, delayMs: delay });
              setTimeout(attach, delay);
            } else if (!closed) {
              log.warn('ds-realtime-exhausted', { collection: collectionPath, tenantId: tid, correlationId });
            }
          },
        );
      } else {
        // Offline fallback: fire once from localStorage, no live updates
        const results = JSON.parse(localStorage.getItem('wc_' + collectionPath) || '[]');
        onUpdate(results, { correlationId });
        unsub = () => {};
      }
    };

    attach();

    return function unsubscribe() {
      if (closed) return;
      closed = true;
      if (unsub) unsub();
      _realtimeGuard.release(tid);
      log.info('ds-realtime-close', { collection: collectionPath, tenantId: tid, correlationId });
      _emitWriteTelemetry(collectionPath, 'subscribe-close', tid, { correlationId });
    };
  },

  async getById(collectionName, docId, tenantId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const _t0 = performance.now();

    if (isConfigured && db) {
      const ref = doc(db, collectionName, docId);
      const snap = await getDoc(ref);
      _recordQueryPerf('query', _t0, collectionName);
      if (!snap.exists()) return null;
      const data = snap.data();
      if (data.tenantId && normalizeTenantId(data.tenantId) !== tid) return null;
      if (data.isDeleted) return null;
      return { id: snap.id, ...data };
    }

    const all = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]');
    return all.find(d => d.id === docId && !d.isDeleted) || null;
  },

  /**
   * TMS-E2-RATE-INTEL-RUNTIME-1 (2026-05-28):
   * Alias method matching the call signature used by 8+ services across TMS, FIN,
   * IT, Settings, and Connectors that expect `DataService.getDoc(path, id, tenantId)`.
   * Before this method was added, every one of those call sites threw
   * "DataService.getDoc is not a function" at runtime — most visibly on
   * /tms/rate-intelligence (TMS-SEAT-E2-1 finding P0-1).
   *
   * Behavior matches getById exactly (tenant-scoped read, soft-delete aware,
   * localStorage fallback when Firestore not configured).
   */
  async getDoc(collectionName, docId, tenantId) {
    return this.getById(collectionName, docId, tenantId);
  },

  /**
   * ELYON-SHELL-TENANT-CUTOVER-1 (2026-05-31):
   * Reads the tenant's v2 config from `tenant_configs/{tenantId}` (seeded by
   * scripts/seed-tenant-configs.js) and normalizes the `modules[{id,enabled,order}]`
   * array into the flat, order-respecting `enabled_modules[]` string list the
   * shell's ModuleLoader/AppRouter consume. Returns null when no config exists
   * (the shell falls back to /welcome — no regression).
   *
   * The doc id IS the tenantId, and the doc carries a matching `tenantId` field,
   * so getById's tenant-scope check passes.
   */
  async getTenantConfig(tenantId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const config = await this.getById('tenant_configs', tid, tid);
    if (!config) return null;
    const enabled_modules = Array.isArray(config.modules)
      ? config.modules
          .filter((m) => m && m.enabled)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((m) => m.id)
      : (config.enabled_modules ?? []);
    return { ...config, enabled_modules };
  },

  async getNextDocNumber(prefix) {
    return getNextDocNumber(prefix);
  },

  // ── TYPES-1: Typed read/write ──────────────────────────────────────────────
  // New code should use these. Existing get()/set() stay for backwards compat.

  /**
   * Read a document and validate it against a Zod schema.
   * Reads are in DIAGNOSTIC mode: a schema mismatch logs a warning and emits
   * a validation_telemetry event but still returns the raw data so callers
   * are never blocked by a schema drift.
   *
   * @param {string} collectionName
   * @param {string} docId
   * @param {import('zod').ZodTypeAny} schema
   * @param {string} [tenantId]
   * @returns {Promise<object|null>}
   */
  async getTyped(collectionName, docId, schema, tenantId) {
    const data = await this.getById(collectionName, docId, tenantId);
    if (data === null) return null;

    const result = schema.safeParse(data);
    if (!result.success) {
      const schemaName = schema.description || collectionName;
      const reasonCode = _zodReasonCode(result.error);
      _emitValidationTelemetry({
        tenantId: data.tenantId || tenantId || 'unknown',
        callerModule: 'DataService.getTyped',
        schemaName,
        reasonCode,
      });
    }
    return data;
  },

  /**
   * Write a document after enforcing schema validation.
   * Writes are in ENFORCEMENT mode: a schema mismatch throws a typed error
   * and the write never reaches Firestore.
   *
   * @param {string} collectionName
   * @param {string|null} docId  — null to auto-generate via create()
   * @param {object} data
   * @param {import('zod').ZodTypeAny} schema
   * @param {string} [userId]
   * @returns {Promise<object>}
   */
  async setTyped(collectionName, docId, data, schema, userId = 'system') {
    const result = schema.safeParse(data);
    if (!result.success) {
      const schemaName = schema.description || collectionName;
      const reasonCode = _zodReasonCode(result.error);
      _emitValidationTelemetry({
        tenantId: data?.tenantId || 'unknown',
        callerModule: 'DataService.setTyped',
        schemaName,
        reasonCode,
      });
      throw new DataValidationError(schemaName, reasonCode, result.error.issues);
    }

    if (docId) {
      return this.update(collectionName, docId, result.data, userId);
    }
    return this.create(collectionName, result.data, userId);
  },

  // ── Tenants collection (SYS1) ──────────────────────────────────────────
  // Tenants are the master customer registry. They are not themselves
  // tenant-scoped (obviously) and bypass the default tenantId filter.

  async getTenant(tenantId) {
    const tid = normalizeTenantId(tenantId);
    if (isConfigured && db) {
      const ref = doc(db, 'tenants', tid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data();
      if (data.softDeleted || data.isDeleted) return null;
      return { id: snap.id, tenantId: snap.id, ...data };
    }
    const all = JSON.parse(localStorage.getItem('wc_tenants') || '[]');
    const row = all.find(t => t.tenantId === tid && !t.softDeleted && !t.isDeleted);
    return row || null;
  },

  async listTenants() {
    if (isConfigured && db) {
      const snap = await getDocs(collection(db, 'tenants'));
      return snap.docs
        .map(d => ({ id: d.id, tenantId: d.id, ...d.data() }))
        .filter(t => !t.softDeleted && !t.isDeleted);
    }
    const all = JSON.parse(localStorage.getItem('wc_tenants') || '[]');
    return all.filter(t => !t.softDeleted && !t.isDeleted);
  },

  async upsertTenant(tenantId, data, userId = 'system') {
    const tid = normalizeTenantId(tenantId);
    _emitWriteTelemetry('tenants', 'upsert', tid);
    const now = new Date().toISOString();
    const record = {
      ...data,
      tenantId: tid,
      updatedAt: isConfigured && db ? serverTimestamp() : now,
      updatedBy: userId,
      softDeleted: data.softDeleted ?? false,
    };

    if (isConfigured && db) {
      const ref = doc(db, 'tenants', tid);
      const existing = await getDoc(ref);
      if (!existing.exists()) {
        record.createdAt = serverTimestamp();
        record.createdBy = userId;
      }
      await setDoc(ref, record, { merge: true });
      return { id: tid, tenantId: tid, ...record };
    }

    const all = JSON.parse(localStorage.getItem('wc_tenants') || '[]');
    const idx = all.findIndex(t => t.tenantId === tid);
    if (idx >= 0) {
      all[idx] = { ...all[idx], ...record };
    } else {
      all.push({ ...record, id: tid, createdAt: now, createdBy: userId });
    }
    localStorage.setItem('wc_tenants', JSON.stringify(all));
    return all.find(t => t.tenantId === tid);
  },

  async setTenantStatus(tenantId, status, userId = 'system') {
    return this.upsertTenant(tenantId, { status }, userId);
  },

  async softDeleteTenant(tenantId, userId = 'system') {
    return this.upsertTenant(tenantId, { softDeleted: true, status: 'churned' }, userId);
  },

  // ── Deployment Mode helpers (DEPLOYMENTMODE-1) ─────────────────────────
  // Five modes: 5pl | in_tenant | 4pl | 3pl | managed_transport
  // Every routing decision that depends on deployment mode calls these helpers
  // rather than reading the tenant doc directly, so the enum logic lives in
  // one place.

  VALID_DEPLOYMENT_MODES: ['5pl', 'in_tenant', '4pl', '3pl', 'managed_transport'],

  validateDeploymentMode(mode) {
    if (!this.VALID_DEPLOYMENT_MODES.includes(mode)) {
      throw new Error(
        `Invalid deploymentMode "${mode}". Must be one of: ${this.VALID_DEPLOYMENT_MODES.join(', ')}`
      );
    }
    return true;
  },

  async getDeploymentMode(tenantId) {
    const t = await this.getTenant(tenantId);
    return t?.deploymentMode || null;
  },

  async isInternalBrokerage(tenantId) {
    const mode = await this.getDeploymentMode(tenantId);
    return mode === '3pl' || mode === '4pl';
  },

  async getAnchorCustomer(tenantId) {
    const t = await this.getTenant(tenantId);
    if (t?.deploymentMode !== '5pl') return null;
    return t?.deploymentConfig?.anchorCustomerId || null;
  },

  async getTenantCapabilities(tenantId) {
    const t = await this.getTenant(tenantId);
    const mode = t?.deploymentMode;
    return {
      brokerage:        mode === '3pl' || mode === '4pl',
      managedTransport: mode === 'managed_transport',
      embeddedSC:       mode === '5pl',
      selfHosted:       mode === 'in_tenant',
      externalSaaS:     t?.revenueEngine === 'external_saas' || t?.revenueEngine === 'both',
      internalBrokerage:t?.revenueEngine === 'internal_brokerage' || t?.revenueEngine === 'both',
      pricingTier:      t?.pricingTier || 'tenant',
      deploymentMode:   mode || null,
    };
  },

  // ── Audit log ───────────────────────────────────────────────────────────
  // Append-only. Never updated, never deleted.

  async appendAudit({ action, entityType, entityId = null, changes = null, userId, tenantId }) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    _emitWriteTelemetry('audit_log', 'create', tid);
    const payload = {
      tenantId: tid,
      userId: userId || 'system',
      action,
      entityType,
      entityId,
      changes,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      timestamp: isConfigured && db ? serverTimestamp() : new Date().toISOString(),
    };

    if (isConfigured && db) {
      await addDoc(collection(db, 'audit_log'), payload);
      return payload;
    }
    const key = 'wc_audit_log';
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    all.push({ ...payload, id: 'audit_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) });
    localStorage.setItem(key, JSON.stringify(all));
    return payload;
  },

  async listAudit(tenantId, { limit: maxRows = 100 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (isConfigured && db) {
      const q = query(
        collection(db, 'audit_log'),
        where('tenantId', '==', tid),
        orderBy('timestamp', 'desc'),
        limit(maxRows),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
    const all = JSON.parse(localStorage.getItem('wc_audit_log') || '[]');
    return all
      .filter(a => normalizeTenantId(a.tenantId) === tid)
      .sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)))
      .slice(0, maxRows);
  },

  // ── Error incident writer — ERROR-COLLECTION-SUBSTRATE-1 ───────────────────
  // Appends an error incident to the top-level `errors` collection.
  // Client-side crash reporters (ERR-001 ErrorBoundary) call this method.
  // incidentId is generated by the client (UUID v4) to deduplicate retries.
  // Firestore rule: allow create if authenticated + own actorUid + softDeleted=false.

  async logError({ tenantId, actorUid, incidentId, errorMessage, stackTrace, componentStack, routePath, severity = 'error', userAgent, appVersion }) {
    const VALID_SEVERITIES = ['info', 'warn', 'error', 'fatal'];
    if (!VALID_SEVERITIES.includes(severity)) severity = 'error';
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const payload = {
      incidentId:      incidentId || `err_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      tenantId:        tid,
      actorUid:        actorUid || 'unknown',
      errorMessage:    errorMessage || 'Unknown error',
      stackTrace:      stackTrace   || null,
      componentStack:  componentStack || null,
      routePath:       routePath    || (typeof window !== 'undefined' ? window.location.pathname : null),
      userAgent:       userAgent    || (typeof navigator !== 'undefined' ? navigator.userAgent : null),
      appVersion:      appVersion   || null,
      severity,
      timestamp:       isConfigured && db ? serverTimestamp() : new Date().toISOString(),
      softDeleted:     false,
    };
    if (isConfigured && db) {
      await addDoc(collection(db, 'errors'), payload);
      return payload;
    }
    const key = 'wc_errors';
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    all.push({ ...payload, id: 'err_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) });
    localStorage.setItem(key, JSON.stringify(all));
    return payload;
  },

  // ── Integration audit writer — SET-006-INTEGRATION-AUDIT-SUBSTRATE-1 ──────
  // Appends an audit_log entry for partner API integration lifecycle events.
  // Actions: connected | disconnected | rotated | scope_changed |
  //          health_status_changed | error_logged
  // KAUDIT-7 contract: settings_category='integrations', visibility='tenant_admin'.

  async writeIntegrationAuditEvent({ tenantId, integrationId, action, actorUid, outcome, details = {} }) {
    const VALID_ACTIONS = ['connected', 'disconnected', 'rotated', 'scope_changed', 'health_status_changed', 'error_logged'];
    if (!VALID_ACTIONS.includes(action)) {
      throw new Error(`DataService.writeIntegrationAuditEvent: unknown action "${action}". Valid: ${VALID_ACTIONS.join(', ')}`);
    }
    const tid = normalizeTenantId(tenantId || storeTenantId());
    _emitWriteTelemetry('audit_log', 'create', tid);
    const payload = {
      tenantId: tid,
      actorUid: actorUid || 'system',
      subjectUid: tid,
      action,
      subject: integrationId,
      category: 'settings',
      settings_category: 'integrations',
      visibility: 'tenant_admin',
      outcome: outcome || 'success',
      severity: outcome === 'failure' ? 'warn' : 'info',
      payload: { integrationId, action, ...details },
      evidence_refs: details.evidence_refs || [],
      timestamp: isConfigured && db ? serverTimestamp() : new Date().toISOString(),
      softDeleted: false,
    };

    if (isConfigured && db) {
      await addDoc(collection(db, 'audit_log'), payload);
      return payload;
    }
    const key = 'wc_audit_log';
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    all.push({ ...payload, id: 'audit_integ_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6) });
    localStorage.setItem(key, JSON.stringify(all));
    return payload;
  },

  // ── Integration records — tenants/{tenantId}/integrations subcollection ─────
  // SET-006-INTEGRATION-AUDIT surface read path. Substrate writes health fields;
  // this reads them for the Settings → Integrations UI.

  async listIntegrationRecords(tenantId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (isConfigured && db) {
      const snap = await getDocs(collection(db, 'tenants', tid, 'integrations'));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
    return JSON.parse(localStorage.getItem(`wc_integrations_${tid}`) || '[]');
  },

  async getIntegrationRecord(tenantId, integrationId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (isConfigured && db) {
      const snap = await getDoc(doc(db, 'tenants', tid, 'integrations', integrationId));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }
    const all = JSON.parse(localStorage.getItem(`wc_integrations_${tid}`) || '[]');
    return all.find(r => r.id === integrationId) || null;
  },

  // ── Compliance — compliance_controls + soc2_readiness_scores ──────────────
  // Hard-capped to the supplied tenantId at the DataService layer.
  // Production-tenant activation requires a separate supervised seat.

  async getComplianceControls(tenantId) {
    const tid = normalizeTenantId(tenantId);
    return this.queryFlex('compliance_controls', [
      { field: 'tenantId',    op: '==',  value: tid },
      { field: 'isDeleted',   op: '==',  value: false },
      { field: 'softDeleted', op: '==',  value: null },
    ]);
  },

  async setComplianceControl(tenantId, controlId, data) {
    const tid = normalizeTenantId(tenantId);
    if (!controlId) throw new Error('setComplianceControl: controlId is required');
    const record = { ...data, tenantId: tid };
    return this.setDoc('compliance_controls', controlId, record);
  },

  async appendReadinessScore(tenantId, scoreDoc) {
    const tid = normalizeTenantId(tenantId);
    return this.create('soc2_readiness_scores', { ...scoreDoc, tenantId: tid }, 'comp-1-engine', 'kfol-comp-1');
  },

  // ── VM-AEGIS-16-FINISH-1: AEGIS-First Home attention + conversations ──

  /**
   * Tenant attention counts for the greeting context line on /ask.
   * Returns the three signals the spec calls out: loads needing attention,
   * invoices waiting, exceptions open. Each query is bounded and tenant-scoped.
   * Failures degrade silently per signal — the surface still renders "all clear".
   */
  async getAttentionCounts({ tenantId } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const safe = async (fn) => {
      try { return await fn(); } catch { return 0; }
    };
    const [loadsNeedingAttention, invoicesWaiting, exceptionsOpen] = await Promise.all([
      safe(() => this.queryFlex('loads', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: 'in', value: ['exception', 'late', 'at_risk'] },
        { field: 'softDeleted', op: '==', value: null },
      ]).then(rows => rows.length)),
      safe(() => this.queryFlex('invoices', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: 'in', value: ['pending_approval', 'dispute', 'awaiting_review'] },
        { field: 'softDeleted', op: '==', value: null },
      ]).then(rows => rows.length)),
      safe(() => this.queryFlex('exceptions', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: '==', value: 'open' },
        { field: 'softDeleted', op: '==', value: null },
      ]).then(rows => rows.length)),
    ]);
    return { loadsNeedingAttention, invoicesWaiting, exceptionsOpen };
  },

  /**
   * Recent notable conversations for the AEGIS-First Home rail.
   * Notable = AEGIS marked actionable OR operator pinned.
   * Tenant- AND user-scoped — operators see only their own threads.
   */
  async getNotableConversations({ tenantId, userId, limit: maxRows = 3 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (!userId) return [];
    return this.queryFlex('aegis_conversations', [
      { field: 'tenantId',    op: '==', value: tid },
      { field: 'userId',      op: '==', value: userId },
      { field: 'notable',     op: '==', value: true },
      { field: 'softDeleted', op: '==', value: null },
    ], { orderBy: 'updatedAt', direction: 'desc', limit: maxRows });
  },

  /* ──────────────────────────────────────────────────────────────────────
   * ELYON-COMMAND-CENTER-V2-FOUNDATION-1 — command-center data layer (§3.5)
   *
   * Four tenant-scoped, entity-preview reads backing the 2×2 body-card grid.
   * Each returns normalized preview rows (NOT count chips — research §1.3 /
   * §5.6 F6) so the operator can answer "do I need to act?" without clicking.
   *
   * Real Firestore queries via the queryFlex adapter; each degrades to []/null
   * on failure so a single card error never breaks the surface (§4.9). Live
   * rows populate once ELYON-DEMO-TENANT-CONFIG-1 seeds the elyon-demo tenant;
   * until then these return empty and cards render their empty states (G8).
   * ────────────────────────────────────────────────────────────────────── */

  /**
   * At-risk shipments — loads whose status signals operator attention, mapped
   * to entity previews with a human-readable risk delta (ETA shift, carrier
   * silence, etc.). Bounded to 5 rows for the above-fold card.
   */
  async getAtRiskShipments(tenantId, { limit: maxRows = 5 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    try {
      const rows = await this.queryFlex('loads', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: 'in', value: ['at_risk', 'late', 'exception'] },
        { field: 'softDeleted', op: '==', value: null },
      ], { orderBy: 'updatedAt', direction: 'desc', limit: maxRows });
      return rows.map((r) => ({
        id:          r.id,
        loadId:      r.loadNumber ?? r.loadId ?? r.id,
        origin:      r.originCity ?? r.origin?.city ?? r.origin ?? '—',
        destination: r.destinationCity ?? r.destination?.city ?? r.destination ?? '—',
        status:      r.status ?? 'at_risk',
        riskLevel:   r.riskLevel ?? (r.status === 'exception' ? 'danger' : 'warning'),
        // ARGUS-sourced human delta (ETA shift / dwell / silence). Seed writes
        // deltaIndicator; older docs use riskDelta/riskReason (WORLD-CLASS §2.2).
        delta:       r.riskDelta ?? r.riskReason ?? r.deltaIndicator ?? null,
        riskSource:  r.riskSource ?? null,
        // COMMAND-CENTER-IMPACT-ANALYSIS-1 — consequence projection (Blue Yonder
        // "impact not status"). Surfaced ONLY from real load financials; left null
        // when the record carries none, so the card degrades to status+delta and
        // never fabricates a dollar figure (REALNESS-PROBE-1 P6).
        customerName:  r.customerName ?? r.customer?.name ?? null,
        commodity:     r.commodity ?? null,
        revenueAtRisk: r.financials?.revenue ?? r.revenue ?? null,
        marginPercent: r.financials?.marginPercent
                         ?? (typeof r.financials?.margin === 'number' && r.financials?.revenue
                              ? Math.round((r.financials.margin / r.financials.revenue) * 100)
                              : null),
        // Provenance: the ARGUS signal that flagged this load (REALNESS-PROBE-1 P5).
        argusAlertId:  r.argusAlertId ?? null,
      }));
    } catch {
      return [];
    }
  },

  /**
   * Pending approvals — AEGIS-proposed actions awaiting human ratification
   * (Pattern Library v1 Pattern 5: Approval Queue). Each row carries enough
   * context to render the confidence dot + aging indicator on the card.
   */
  async getPendingApprovals(tenantId, { limit: maxRows = 5 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    try {
      const rows = await this.queryFlex('approval_queue', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: '==', value: 'pending' },
        { field: 'softDeleted', op: '==', value: null },
      ], { orderBy: 'proposedAt', direction: 'desc', limit: maxRows });
      return rows.map((r) => ({
        id:          r.id,
        entityId:    r.entityId ?? r.targetId ?? r.id,
        entityType:  r.entityType ?? r.actionType ?? 'action',
        title:       r.title ?? r.summary ?? 'AEGIS-proposed action',
        requester:   r.requester ?? r.proposedBy ?? 'AEGIS',
        ageHours:    r.ageHours ?? _ageHoursFrom(r.proposedAt ?? r.createdAt),
        confidence:  typeof r.confidence === 'number' ? r.confidence : null,
        route:       r.route ?? null,
        // Agentic provenance (WORLD-CLASS §2.2 / §4.2): autonomy tier label,
        // EXEC rationale, and the Decision Twin attestation ref for "Why?".
        autonomyTier:    r.autonomyTier ?? r.autonomy ?? null,
        rationale:       r.rationale ?? null,
        decisionTwinRef: r.decisionTwinRef ?? null,
        agentName:       r.agentName ?? r.requester ?? r.proposedBy ?? 'AEGIS',
      }));
    } catch {
      return [];
    }
  },

  /**
   * Open exceptions — operational exceptions still requiring resolution,
   * mapped to severity-tagged preview rows (semantic feedback tokens only).
   */
  async getOpenExceptions(tenantId, { limit: maxRows = 5 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    try {
      const rows = await this.queryFlex('exceptions', [
        { field: 'tenantId', op: '==', value: tid },
        { field: 'status',   op: '==', value: 'open' },
        { field: 'softDeleted', op: '==', value: null },
      ], { orderBy: 'detectedAt', direction: 'desc', limit: maxRows });
      return rows.map((r) => ({
        id:        r.id,
        type:      r.exceptionType ?? r.type ?? 'exception',
        entityId:  r.entityId ?? r.loadId ?? r.id,
        ageHours:  r.ageHours ?? _ageHoursFrom(r.detectedAt ?? r.createdAt),
        severity:  r.severity ?? 'warning',  // danger | warning | info
      }));
    } catch {
      return [];
    }
  },

  /**
   * COMMAND-CENTER-COLLAB-AUDIT-TRAIL-1 — in-surface collaboration on a command-
   * center entity (approval / at-risk load / exception), replacing email threads
   * with an auditable in-platform record (Blue Yonder T1). Returns the comment
   * trail oldest→newest. Tenant- AND entity-scoped. Soft-deleted comments are
   * excluded but never hard-deleted (architecture: never hard delete) — the
   * append-only trail IS the audit trail.
   */
  async getEntityComments(tenantId, entityType, entityId, { limit: maxRows = 50 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (!entityType || !entityId) return [];
    try {
      const rows = await this.queryFlex('entity_comments', [
        { field: 'tenantId',    op: '==', value: tid },
        { field: 'entityType',  op: '==', value: String(entityType) },
        { field: 'entityId',    op: '==', value: String(entityId) },
        { field: 'softDeleted', op: '==', value: null },
      ], { orderBy: 'createdAt', direction: 'asc', limit: maxRows });
      return rows.map((r) => ({
        id:         r.id,
        body:       r.body ?? '',
        authorId:   r.authorId ?? r.createdBy ?? null,
        authorName: r.authorName ?? 'Operator',
        createdAt:  r.createdAt ?? null,
      }));
    } catch {
      return [];
    }
  },

  /**
   * Append a collaboration comment to an entity. Goes through DataService.create
   * so createDocument stamps createdAt/createdBy/source — the attributed,
   * timestamped, append-only audit trail (KAUDIT-aligned). Body is required and
   * trimmed; empty bodies are rejected (no blank audit rows). Returns the created
   * comment, or null on invalid input.
   */
  async addEntityComment(tenantId, { entityType, entityId, authorId, authorName, body } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const text = String(body ?? '').trim();
    if (!entityType || !entityId || !text) return null;
    const created = await this.create('entity_comments', {
      tenantId:    tid,
      entityType:  String(entityType),
      entityId:    String(entityId),
      authorId:    authorId ?? null,
      authorName:  authorName ?? 'Operator',
      body:        text,
      softDeleted: null,
    }, authorId ?? 'operator', 'collab');
    return {
      id:         created.id,
      body:       created.body,
      authorId:   created.authorId ?? created.createdBy ?? null,
      authorName: created.authorName ?? 'Operator',
      createdAt:  created.createdAt ?? null,
    };
  },

  /**
   * Operations brief — the AEGIS-generated daily overview. Returns ≤4 summary
   * lines plus an optional expandable detail body (research §10.4 / G4: never
   * a verbose paragraph in the default state). Null when no brief exists.
   */
  async getOperationsBrief(tenantId) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    try {
      // NOTE: do NOT constrain softDeleted in the Firestore query. The hero seed
      // writes briefs with `softDeleted: false`, and Firestore `== null` does not
      // match `false` — the brief would silently vanish and the hero open empty
      // (WORLD-CLASS §5 / REALNESS P6). Fetch recent briefs, then exclude only a
      // truthy soft-delete sentinel in memory.
      const rows = await this.queryFlex('operations_briefs', [
        { field: 'tenantId', op: '==', value: tid },
      ], { orderBy: 'generatedAt', direction: 'desc', limit: 3 });
      const brief = rows.find((r) => r.softDeleted !== true && r.isDeleted !== true);
      if (!brief) return null;
      const lines = Array.isArray(brief.lines)
        ? brief.lines.slice(0, 4)
        : String(brief.summary ?? '').split('\n').filter(Boolean).slice(0, 4);
      return {
        id:        brief.id,
        lines,
        detail:    brief.detail ?? null,
        // Narrative-first synthesis + provenance for AegisNarrativeHero
        // (WORLD-CLASS §2.1 / §4.2): the synthesized read, not a tally.
        narrativeText: brief.narrativeText ?? null,
        confidence:    typeof brief.confidence === 'number' ? brief.confidence : null,
        generatedBy:   brief.generatedBy ?? 'AEGIS',
        sourceRefs:    Array.isArray(brief.sourceRefs) ? brief.sourceRefs : [],
        updatedAt: brief.generatedAt ?? brief.updatedAt ?? null,
      };
    } catch {
      return null;
    }
  },

  /**
   * AEGIS-handled-today — autonomous actions AEGIS completed today without
   * needing approval (auto-tender, auto-clear, auto-notify). Feeds the hero's
   * "AEGIS handled today" count + expandable list (WORLD-CLASS §2.1 / §4.2).
   *
   * Queries on tenantId equality ONLY (auto-indexed; no composite index, so no
   * missing-index failure in the demo), then filters to today + non-deleted and
   * orders by recency in memory. The seed writes `softDeleted: false`, so we
   * exclude only a truthy sentinel — never `== null`.
   */
  async getAegisActionsToday(tenantId, { limit: maxRows = 12 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    try {
      const rows = await this.queryFlex('aegis_actions', [
        { field: 'tenantId', op: '==', value: tid },
      ]);
      const startOfDay = new Date();
      startOfDay.setHours(0, 0, 0, 0);
      const since = startOfDay.getTime();
      return rows
        .filter((r) => r.softDeleted !== true && r.isDeleted !== true)
        .filter((r) => r.status === 'executed')
        .filter((r) => {
          const t = Date.parse(r.executedAt ?? r.createdAt ?? r.updatedAt ?? '');
          return Number.isNaN(t) ? true : t >= since;
        })
        .sort((a, b) =>
          String(b.createdAt ?? b.updatedAt ?? '').localeCompare(String(a.createdAt ?? a.updatedAt ?? '')))
        .slice(0, maxRows)
        .map((r) => ({
          id:           r.id,
          actionType:   r.actionType ?? 'action',
          module:       r.module ?? null,
          autonomyTier: r.autonomyTier ?? r.autonomy ?? null,
          outcome:      r.outcome ?? r.observation ?? '',
          rationale:    r.rationale ?? null,
          entityId:     r.triggerEntityId ?? r.entityId ?? null,
          entityType:   r.triggerEntityType ?? null,
          confidence:   typeof r.confidence === 'number' ? r.confidence : null,
          at:           r.executedAction?.acceptedAt ?? r.updatedAt ?? r.createdAt ?? null,
        }));
    } catch {
      return [];
    }
  },

  /**
   * KPI 30-day series — the sparkline + trend substrate for BrandedKPICard
   * (FLOOR-VIS-004). One snapshot doc per day; returns the ordered points plus
   * a per-metric series array. tenantId-equality only (auto-indexed); excludes
   * a truthy soft-delete sentinel; orders by date ascending in memory.
   */
  async getKpiSeries(tenantId, { days = 30 } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const METRICS = ['at_risk_count', 'exceptions_open', 'approvals_cleared', 'on_time_pct'];
    const empty = { points: [], series: Object.fromEntries(METRICS.map((m) => [m, []])) };
    try {
      const rows = await this.queryFlex('kpi_snapshots', [
        { field: 'tenantId', op: '==', value: tid },
      ]);
      const points = rows
        .filter((r) => r.softDeleted !== true && r.isDeleted !== true)
        .sort((a, b) => String(a.date ?? '').localeCompare(String(b.date ?? '')))
        .slice(-days);
      const series = {};
      for (const m of METRICS) {
        series[m] = points.map((d) => ({ date: d.date ?? null, value: Number(d[m] ?? 0) }));
      }
      // todayValues: convenience scalar map of latest data point for each metric.
      // Consumers use series[m] arrays for sparklines; todayValues[m] for tile headlines.
      const todayValues = Object.fromEntries(
        METRICS.map((m) => [m, points.at(-1)?.[m] ?? null])
      );
      return { points, series, todayValues };
    } catch {
      return empty;
    }
  },

  /**
   * Persist an AEGIS conversation turn (create new or append to existing).
   * Returns the conversation document. The `notable` flag is set when the
   * response includes an action execution OR when the operator pins the row.
   */
  async saveAegisConversation({ tenantId, userId, conversationId, query: q, response, sources = [], actions = [], notable = false } = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    if (!userId) throw new Error('saveAegisConversation: userId required for tenant + user RBAC');
    const messageEntry = { role: 'user', text: q, ts: Date.now() };
    const responseEntry = { role: 'aegis', text: response, sources, actions, ts: Date.now() };
    if (conversationId) {
      const existing = await this.getById('aegis_conversations', conversationId, tid);
      const messages = Array.isArray(existing?.messages) ? existing.messages : [];
      const next = {
        ...existing,
        messages: [...messages, messageEntry, responseEntry],
        notable: notable || existing?.notable || actions.length > 0,
        lastQuery: q,
        lastResponseAt: Date.now(),
      };
      await this.update('aegis_conversations', conversationId, next, userId);
      return { id: conversationId, ...next };
    }
    return this.create('aegis_conversations', {
      tenantId: tid,
      userId,
      messages: [messageEntry, responseEntry],
      notable: notable || actions.length > 0,
      firstQuery: q,
      lastQuery: q,
      lastResponseAt: Date.now(),
    }, userId, 'aegis-first-home');
  },

  /**
   * Soft-delete a conversation (kebab menu → Delete on the rail).
   * Hard delete is forbidden by the architecture rule.
   */
  async softDeleteAegisConversation(conversationId, userId) {
    return this.update('aegis_conversations', conversationId,
      { softDeleted: new Date().toISOString() }, userId);
  },

  /**
   * Pin/unpin a conversation — the kebab Pin action toggles `notable`.
   */
  async setAegisConversationNotable(conversationId, userId, notable) {
    return this.update('aegis_conversations', conversationId, { notable: !!notable }, userId);
  },

  // ── IoT device + alert + ingestion + agentic methods (ELYON-IOT-V2-PR1-1) ──
  // Software-pure (D-028): no hardware keys here; vendor calls are Cloud-Function
  // proxied. tenantId required on every document; soft-delete only (isDeleted).
  // aegis_decisions writes are REAL (patent surface F-ADVERSARIAL-TWIN-001).

  /** Apply array/equality filters client-side after a tenant-scoped read. */
  _iotFilter(rows, filters = {}) {
    return (rows || []).filter((r) => {
      for (const [k, v] of Object.entries(filters)) {
        if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) continue;
        if (k === 'hasAlerts') { if ((Number(r.alertCount) > 0) !== !!v) return false; continue; }
        if (Array.isArray(v)) { if (!v.includes(r[k])) return false; continue; }
        if (r[k] !== v) return false;
      }
      return true;
    });
  },

  // devices/{deviceId}
  async getDevices(tenantId, filters = {}) {
    const rows = await this.query('devices', {}, tenantId);
    return this._iotFilter(rows, filters);
  },
  async getDevice(tenantId, deviceId) {
    return this.getById('devices', deviceId, tenantId);
  },
  async createDevice(tenantId, data, userId = 'system') {
    return this.create('devices', { ...data, tenantId }, userId, 'iot');
  },
  async updateDevice(tenantId, deviceId, data, userId = 'system') {
    return this.update('devices', deviceId, { ...data, tenantId }, userId);
  },
  async softDeleteDevice(tenantId, deviceId, userId = 'system') {
    return this.delete('devices', deviceId, userId);
  },

  // device_alerts/{alertId}
  async getDeviceAlerts(tenantId, filters = {}) {
    const rows = await this.query('device_alerts', {}, tenantId);
    return this._iotFilter(rows, filters);
  },
  async createDeviceAlert(tenantId, data, userId = 'system') {
    return this.create('device_alerts', { ...data, tenantId, state: data.state || 'active' }, userId, 'iot');
  },
  async acknowledgeAlert(tenantId, alertId, operatorId) {
    return this.update('device_alerts', alertId, {
      tenantId, state: 'acknowledged', acknowledgedBy: operatorId, acknowledgedAt: new Date().toISOString(),
    }, operatorId);
  },
  async resolveAlert(tenantId, alertId, operatorId = 'system') {
    return this.update('device_alerts', alertId, {
      tenantId, state: 'resolved', resolvedAt: new Date().toISOString(),
    }, operatorId);
  },

  // iot_alerts/{alertId} — canonical fired-alert collection (written by alertRuleEvaluator CF)
  async getIotAlerts(tenantId, filters = {}) {
    const rows = await this.queryFlex('iot_alerts', [
      { field: 'tenantId',    op: '==', value: tenantId },
      { field: 'softDeleted', op: '==', value: false },
    ]);
    return this._iotFilter(rows, filters);
  },
  async acknowledgeIotAlert(tenantId, alertId, operatorId = 'system') {
    return this.update('iot_alerts', alertId, { tenantId, state: 'acknowledged', acknowledgedBy: operatorId, acknowledgedAt: new Date().toISOString() }, operatorId);
  },
  async resolveIotAlert(tenantId, alertId, operatorId = 'system') {
    return this.update('iot_alerts', alertId, { tenantId, state: 'resolved', lifecycleState: 'resolved', resolvedAt: new Date().toISOString(), resolvedBy: operatorId }, operatorId);
  },
  async triageIotAlert(tenantId, alertId, operatorId = 'system', notes = '') {
    return this.update('iot_alerts', alertId, { tenantId, lifecycleState: 'triaged', triagedBy: operatorId, triagedAt: new Date().toISOString(), notes }, operatorId);
  },
  async assignIotAlert(tenantId, alertId, assigneeId, operatorId = 'system') {
    return this.update('iot_alerts', alertId, { tenantId, lifecycleState: 'assigned', assignedTo: assigneeId, assignedBy: operatorId, assignedAt: new Date().toISOString() }, operatorId);
  },
  async bulkAcknowledgeIotAlerts(tenantId, alertIds, operatorId = 'system') {
    const now = new Date().toISOString();
    return Promise.all(alertIds.map(alertId =>
      this.update('iot_alerts', alertId, { tenantId, state: 'acknowledged', lifecycleState: 'acknowledged', acknowledgedBy: operatorId, acknowledgedAt: now }, operatorId)
    ));
  },
  async getZoneStates(tenantId) {
    return this.queryFlex('device_zone_states', [{ field: 'tenantId', op: '==', value: tenantId }]);
  },

  // zone_definitions/{zoneId} — geofence zones evaluated by zoneEvaluator CF
  async getZones(tenantId) {
    return this.queryFlex('zone_definitions', [
      { field: 'tenantId',    op: '==', value: tenantId },
      { field: 'softDeleted', op: '==', value: false },
      { field: 'enabled',     op: '==', value: true },
    ]);
  },
  async upsertZone(tenantId, data, userId = 'system') {
    if (data.id) return this.update('zone_definitions', data.id, { ...data, tenantId }, userId);
    return this.create('zone_definitions', { ...data, tenantId, enabled: true, softDeleted: false }, userId, 'iot');
  },
  async softDeleteZone(tenantId, zoneId, userId = 'system') {
    return this.delete('zone_definitions', zoneId, userId);
  },

  // alert_rules/{ruleId}
  async getAlertRules(tenantId) {
    return this.query('alert_rules', {}, tenantId);
  },
  async createAlertRule(tenantId, data, userId = 'system') {
    return this.create('alert_rules', { ...data, tenantId, enabled: data.enabled !== false }, userId, 'iot');
  },
  async updateAlertRule(tenantId, ruleId, data, userId = 'system') {
    return this.update('alert_rules', ruleId, { ...data, tenantId }, userId);
  },
  async softDeleteAlertRule(tenantId, ruleId, userId = 'system') {
    return this.delete('alert_rules', ruleId, userId);
  },

  // ingestion_configs/{configId}
  async getIngestionConfigs(tenantId) {
    return this.query('ingestion_configs', {}, tenantId);
  },
  async upsertIngestionConfig(tenantId, data, userId = 'system') {
    if (data.id) return this.update('ingestion_configs', data.id, { ...data, tenantId }, userId);
    return this.create('ingestion_configs', { ...data, tenantId }, userId, 'iot');
  },
  /** Per-vendor ingestion health summary derived from ingestion_configs. */
  async getIngestionHealth(tenantId) {
    const configs = await this.query('ingestion_configs', {}, tenantId);
    return (configs || []).map((c) => ({
      vendor: c.vendor,
      status: c.status || 'unconfigured',
      lastSyncAt: c.lastSyncAt || null,
      errorCount: c.errorCount || 0,
      deviceCount: c.deviceCount || 0,
    }));
  },

  // aegis_decisions/{decisionId} — REAL writes (patent surface)
  async createAegisDecision(tenantId, data, userId = 'system') {
    return this.create('aegis_decisions', {
      ...data,
      tenantId,
      acknowledged: false,
      createdAt: data.createdAt || new Date().toISOString(),
    }, userId, 'aegis');
  },
  async getAegisDecision(tenantId, decisionId) {
    return this.getById('aegis_decisions', decisionId, tenantId);
  },
  async getAegisDecisions(tenantId, filters = {}) {
    const rows = await this.query('aegis_decisions', {}, tenantId);
    return this._iotFilter(rows, filters);
  },
  async acknowledgeAegisDecision(tenantId, decisionId, operatorId) {
    return this.update('aegis_decisions', decisionId, {
      tenantId, acknowledged: true, acknowledgedBy: operatorId, acknowledgedAt: new Date().toISOString(),
    }, operatorId);
  },

  // ── Subcollection methods ──
  // Path format: 'appointments/{apptId}/pos' or 'appointments/{apptId}/pos/{poId}/lpns'

  async createSub(parentPath, data, userId = 'system', source = 'manual') {
    const effectiveTenant = normalizeTenantId(data?.tenantId) || storeTenantId();
    const document = createDocument({ ...data, tenantId: effectiveTenant }, userId, source);
    _emitWriteTelemetry(parentPath, 'create', effectiveTenant);

    if (isConfigured && db) {
      const ref = await addDoc(collection(db, parentPath), document);
      return { id: ref.id, ...document };
    }

    document.id = document.id || 'local_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.push(document);
    localStorage.setItem(key, JSON.stringify(existing));
    return document;
  },

  /**
   * Write a document with a caller-supplied ID (no createDocument wrapping).
   * Used by DocService to store pre-built entries with ULID keys.
   */
  async setDoc(collectionName, docId, data) {
    _emitWriteTelemetry(collectionName, 'set', data?.tenantId);
    if (isConfigured && db) {
      const ref = doc(db, collectionName, docId);
      await setDoc(ref, data);
      return { id: docId, ...data };
    }

    const key = 'wc_' + collectionName;
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const idx = existing.findIndex(d => d.id === docId);
    if (idx >= 0) existing[idx] = { ...data, id: docId };
    else existing.push({ ...data, id: docId });
    localStorage.setItem(key, JSON.stringify(existing));
    return { id: docId, ...data };
  },

  /**
   * VIEW-MODE-CLUSTER-HOTFIX-1: alias for `setDoc`.
   *
   * Many call sites across the codebase (viewModeService, demoTenantService,
   * WorkspaceService, fleet-coordinator, marketIntelligenceIngester,
   * ArgusAdminSettings, several Artemis constellations, etc.) invoke
   * `DataService.set(collection, id, data, tenantId?)` rather than `setDoc`.
   * The DENSITY-PR-E picker test mocked `set` directly so the missing real
   * method went unnoticed; in the production bundle the call surfaces as
   * `TypeError: X.set is not a function`.
   *
   * Adding a single alias here fixes every latent caller. The optional
   * `tenantId` arg is honored: if data lacks tenantId we attach it before
   * persistence so tenant-scoped writes stay scoped.
   */
  async set(collectionName, docId, data, tenantId) {
    const payload = (tenantId && data && !data.tenantId)
      ? { ...data, tenantId }
      : data;
    return this.setDoc(collectionName, docId, payload);
  },

  /**
   * VIEW-MODE-CLUSTER-HOTFIX-3: alias for `getById`.
   *
   * Symmetric to the HOTFIX-1 `set` alias. Multiple call sites read documents
   * via `DataService.get(collection, id, tenantId?)` rather than `getById`:
   * viewModeService.loadUserViewMode / loadTenantDefaultViewMode /
   * hasCompletedOnboarding, FraudFindingsList signal lookups, the WMS
   * putaway locationDirective module (pallet/directive/location reads).
   *
   * Without this alias every caller silently fails with
   * `TypeError: DataService.get is not a function`, which the surrounding
   * try/catch blocks swallow; viewModeService then falls through to the
   * tenant-default branch (which itself also fails the same way) and finally
   * returns the product default 'C'. End-user symptom: picking Mode A or B
   * still routes to the Mode C surface because `loadUserViewMode` always
   * resolves to 'C' and `hasCompletedOnboarding` always returns false.
   *
   * The viewModeService callers' unit tests mock `DataService.get` directly,
   * which hid the gap — same failure mode as the original `set` alias.
   * Fixing it as an alias preserves every caller's existing call signature
   * and keeps the change surgical (no caller-site edits, no behavior change
   * for working code paths).
   */
  async get(collectionName, docId, tenantId) {
    return this.getById(collectionName, docId, tenantId);
  },

  /**
   * DATASERVICE-GETALL-SHIM — alias for `query`.
   *
   * Multiple service layers call `DataService.getAll(collection, { tenantId })` or
   * `DataService.getAll(collection, tenantId)` against a method that never existed.
   * Adding a single normalizing shim here fixes every caller without touching them.
   *
   * Call signatures observed in the codebase:
   *   getAll(col, { tenantId })            — opts-object form
   *   getAll(col, tenantId)                — bare-string form
   *   getAll(col, { filters: { ... } })    — filters-in-opts form
   *   getAll(col, tenantId, { limit })     — 3-arg form with extra opts
   */
  async getAll(collectionName, tenantIdOrOpts = {}, extraOpts = {}) {
    let tid, filters = {};
    if (typeof tenantIdOrOpts === 'string') {
      tid = tenantIdOrOpts;
      filters = extraOpts.filters || {};
      if (extraOpts.limit) filters.limit = extraOpts.limit;
    } else {
      tid = tenantIdOrOpts?.tenantId || tenantIdOrOpts?.filters?.tenantId;
      filters = tenantIdOrOpts?.filters || {};
    }
    return this.query(collectionName, filters, tid);
  },

  /**
   * DATASERVICE-GETONE-SHIM — alias for `get` (which aliases `getById`).
   *
   * Call signatures: getOne(col, id, { tenantId }) or getOne(col, id, tenantId)
   */
  async getOne(collectionName, docId, tenantIdOrOpts) {
    const tid = typeof tenantIdOrOpts === 'string'
      ? tenantIdOrOpts
      : (tenantIdOrOpts?.tenantId || null);
    return this.getById(collectionName, docId, tid);
  },

  async updateSub(parentPath, docId, data, userId = 'system') {
    const updates = updateDocument(data, userId);
    _emitWriteTelemetry(parentPath, 'update', data?.tenantId);

    if (isConfigured && db) {
      const ref = doc(db, parentPath, docId);
      await updateDoc(ref, { ...updates, version: increment(1) });
      return { id: docId, ...updates };
    }

    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const idx = existing.findIndex(d => d.id === docId);
    if (idx >= 0) {
      existing[idx] = { ...existing[idx], ...updates, version: (existing[idx].version || 0) + 1 };
      localStorage.setItem(key, JSON.stringify(existing));
    }
    return { id: docId, ...updates };
  },

  async deleteSub(parentPath, docId, userId = 'system') {
    const updates = softDelete(userId);
    _emitWriteTelemetry(parentPath, 'delete');

    if (isConfigured && db) {
      const ref = doc(db, parentPath, docId);
      await updateDoc(ref, updates);
      return { id: docId, ...updates };
    }

    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    const idx = existing.findIndex(d => d.id === docId);
    if (idx >= 0) {
      Object.assign(existing[idx], updates);
      localStorage.setItem(key, JSON.stringify(existing));
    }
    return { id: docId, ...updates };
  },

  async querySub(parentPath, filters = {}) {
    if (isConfigured && db) {
      const constraints = [where('isDeleted', '==', false)];
      if (filters.status) constraints.push(where('status', '==', filters.status));
      if (filters.poNumber) constraints.push(where('poNumber', '==', filters.poNumber));
      constraints.push(orderBy('createdAt', 'desc'));
      if (filters.limit) constraints.push(limit(filters.limit));

      const q = query(collection(db, parentPath), ...constraints);
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    let results = JSON.parse(localStorage.getItem(key) || '[]');
    results = results.filter(d => !d.isDeleted);
    if (filters.status) results = results.filter(d => d.status === filters.status);
    return results;
  },

  /**
   * Return all events_log entries for a given correlationId, sorted by
   * causal order (createdAt asc). Used by EvidenceView.
   */
  async queryEventsForCorrelation(correlationId, tenantId) {
    if (!correlationId || !tenantId) return [];
    const parentPath = 'events_log/' + tenantId + '/events';

    if (isConfigured && db) {
      const q = query(
        collection(db, parentPath),
        where('correlationId', '==', correlationId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'asc'),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    return all
      .filter(e => !e.isDeleted && e.correlationId === correlationId)
      .sort((a, b) => {
        const ta = a.createdAt?.seconds || new Date(a.createdAt || 0).getTime() / 1000;
        const tb = b.createdAt?.seconds || new Date(b.createdAt || 0).getTime() / 1000;
        return ta - tb;
      });
  },

  /**
   * ORCH-1-REPLAY: Cursor-based pagination over a correlationId event chain.
   * Used by EventBusService.getEventChainPage() for DOC-1 Evidence View.
   *
   * cursor — the `createdAt` value of the last event from the previous page
   *          (Firestore Timestamp or numeric seconds). Null/undefined = first page.
   * pageSize — max events per page (default 25).
   *
   * Returns one extra doc internally to determine `hasMore` without a count query.
   */
  async queryEventChainPage(correlationId, tenantId, { cursor = null, pageSize = 25 } = {}) {
    if (!correlationId || !tenantId) return { docs: [], nextCursor: null, hasMore: false };
    const parentPath = 'events_log/' + tenantId + '/events';

    if (isConfigured && db) {
      const constraints = [
        where('correlationId', '==', correlationId),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'asc'),
      ];
      if (cursor !== null && cursor !== undefined) constraints.push(startAfter(cursor));
      constraints.push(limit(pageSize + 1));

      const q = query(collection(db, parentPath), ...constraints);
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const hasMore = all.length > pageSize;
      const page = hasMore ? all.slice(0, pageSize) : all;
      const nextCursor = hasMore ? (page[page.length - 1].createdAt ?? null) : null;
      return { docs: page, nextCursor, hasMore };
    }

    // localStorage fallback (dev / test mode)
    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    const filtered = all
      .filter(e => !e.isDeleted && e.correlationId === correlationId)
      .sort((a, b) => {
        const ta = a.createdAt?.seconds ?? new Date(a.createdAt || 0).getTime() / 1000;
        const tb = b.createdAt?.seconds ?? new Date(b.createdAt || 0).getTime() / 1000;
        return ta - tb;
      });

    let startIdx = 0;
    if (cursor !== null && cursor !== undefined) {
      const cursorSecs = cursor?.seconds ?? (typeof cursor === 'number' ? cursor : 0);
      const after = filtered.findIndex(e => {
        const ts = e.createdAt?.seconds ?? new Date(e.createdAt || 0).getTime() / 1000;
        return ts > cursorSecs;
      });
      startIdx = after < 0 ? filtered.length : after;
    }
    const page = filtered.slice(startIdx, startIdx + pageSize);
    const hasMore = startIdx + pageSize < filtered.length;
    const nextCursor = hasMore ? (page[page.length - 1]?.createdAt ?? null) : null;
    return { docs: page, nextCursor, hasMore };
  },

  /**
   * Real-time subscription to events_log for a tenant.
   * cutoffDate: Date — only events created at or after this point are returned.
   * callback: (events: Array) => void — called on every change.
   * Returns an unsubscribe function.
   *
   * Falls back to polling when Firebase is not configured (dev/localStorage mode).
   */
  subscribeToEventsLog(tenantId, cutoffDate, callback) {
    const parentPath = 'events_log/' + tenantId + '/events';

    if (isConfigured && db) {
      const constraints = [
        where('isDeleted', '==', false),
        where('createdAt', '>=', Timestamp.fromDate(cutoffDate)),
        orderBy('createdAt', 'desc'),
        limit(100),
      ];
      const q = query(collection(db, parentPath), ...constraints);
      return onSnapshot(q, (snap) => {
        const events = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(events);
      }, (err) => {
        console.error('DataService.subscribeToEventsLog error:', err.message);
      });
    }

    // localStorage fallback — poll every 2 seconds
    const poll = async () => {
      try {
        const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
        const all = JSON.parse(localStorage.getItem(key) || '[]');
        const cutoffMs = cutoffDate.getTime();
        const filtered = all
          .filter(e => !e.isDeleted)
          .filter(e => {
            const ts = e.createdAt?.seconds
              ? e.createdAt.seconds * 1000
              : new Date(e.createdAt || 0).getTime();
            return ts >= cutoffMs;
          })
          .sort((a, b) => {
            const ta = a.createdAt?.seconds || 0;
            const tb = b.createdAt?.seconds || 0;
            return tb - ta;
          })
          .slice(0, 100);
        callback(filtered);
      } catch {
        // Non-fatal
      }
    };
    poll();
    const timer = setInterval(poll, 2000);
    return () => clearInterval(timer);
  },

  /**
   * Real-time subscription to doc_entries for a tenant.
   * Scoped to agent_decision entries, ordered newest-first, limit 100.
   * cutoffDate: Date — only entries created at or after this point.
   * callback: (entries: Array) => void — called on every change.
   * Returns an unsubscribe function.
   */
  subscribeToDocEntries(tenantId, cutoffDate, callback) {
    const parentPath = `doc_entries/${tenantId}/entries`;

    if (isConfigured && db) {
      const constraints = [
        where('isDeleted', '==', false),
        where('entryVariant', '==', 'agent_decision'),
        where('createdAt', '>=', Timestamp.fromDate(cutoffDate)),
        orderBy('createdAt', 'desc'),
        limit(100),
      ];
      const q = query(collection(db, parentPath), ...constraints);
      return onSnapshot(q, (snap) => {
        const entries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        callback(entries);
      }, (err) => {
        console.error('DataService.subscribeToDocEntries error:', err.message);
      });
    }

    // localStorage fallback — poll every 3 seconds
    const poll = async () => {
      try {
        const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
        const all = JSON.parse(localStorage.getItem(key) || '[]');
        const cutoffMs = cutoffDate.getTime();
        const filtered = all
          .filter(e => !e.isDeleted && e.entryVariant === 'agent_decision')
          .filter(e => {
            const ts = e.createdAt?.seconds
              ? e.createdAt.seconds * 1000
              : new Date(e.createdAt || 0).getTime();
            return ts >= cutoffMs;
          })
          .sort((a, b) => {
            const ta = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt || 0).getTime();
            const tb = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt || 0).getTime();
            return tb - ta;
          })
          .slice(0, 100);
        callback(filtered);
      } catch {
        // Non-fatal
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  },

  /**
   * Mission Control — real-time feed of doc_entries for a tenant.
   * Subscribes to the flat doc_entries collection (where DocService writes).
   * variants: string[] — entryVariant values to include (default: all AGENT-2 ops variants)
   * pageSize: number — max entries returned (default 50)
   * Returns an unsubscribe function.
   */
  subscribeToDocFeed(tenantId, variants, cutoffDate, callback, pageSize = 50) {
    const DEFAULT_VARIANTS = [
      'workflow_state_transition',
      'agent_handoff',
      'sentinel_violation',
      'escalation_requested',
      'human_override',
    ];
    const activeVariants = variants?.length ? variants : DEFAULT_VARIANTS;
    const tid = normalizeTenantId(tenantId);

    if (isConfigured && db) {
      const constraints = [
        where('tenantId', '==', tid),
        where('entryVariant', 'in', activeVariants),
        where('createdAt', '>=', Timestamp.fromDate(cutoffDate)),
        orderBy('createdAt', 'desc'),
        limit(pageSize),
      ];
      const q = query(collection(db, 'doc_entries'), ...constraints);
      return onSnapshot(q, (snap) => {
        callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
        console.error('DataService.subscribeToDocFeed error:', err.message);
      });
    }

    // localStorage fallback — poll every 3 seconds
    const poll = () => {
      try {
        const all = JSON.parse(localStorage.getItem('wc_doc_entries') || '[]');
        const cutoffMs = cutoffDate.getTime();
        const filtered = all
          .filter(e => normalizeTenantId(e.tenantId) === tid && activeVariants.includes(e.entryVariant))
          .filter(e => {
            const ts = e.createdAt?.seconds
              ? e.createdAt.seconds * 1000
              : new Date(e.createdAt || 0).getTime();
            return ts >= cutoffMs;
          })
          .sort((a, b) => {
            const ta = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : new Date(a.createdAt || 0).getTime();
            const tb = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : new Date(b.createdAt || 0).getTime();
            return tb - ta;
          })
          .slice(0, pageSize);
        callback(filtered);
      } catch {
        // Non-fatal
      }
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  },

  /**
   * KPI-SPARKLINE-1 §3.B: read a timeseries for a sparkline.
   * Reads `kpi_series` filtered by tenantId + kpiId, ordered by `timestamp`,
   * limited to the most recent N points. Returns a numeric array (just the
   * `value` field) ready to feed to <KPISparkline values=... />.
   *
   * @param {string} tenantId
   * @param {string} kpiId
   * @param {{ points?: number, granularity?: 'day'|'week'|'month' }} [opts]
   * @returns {Promise<number[]>}
   */
  async getKPISeries(tenantId, kpiId, opts = {}) {
    const tid = normalizeTenantId(tenantId || storeTenantId());
    const points = Math.max(2, Math.min(opts.points ?? 30, 365));
    const conditions = [
      { field: 'tenantId', op: '==', value: tid },
      { field: 'kpiId', op: '==', value: kpiId },
    ];
    if (opts.granularity) {
      conditions.push({ field: 'granularity', op: '==', value: opts.granularity });
    }
    const rows = await this.queryFlex('kpi_series', conditions, {
      orderBy: 'timestamp',
      direction: 'desc',
      limit: points,
    });
    return rows
      .slice()
      .reverse()
      .map((r) => Number(r.value) || 0);
  },

  async getSubById(parentPath, docId) {
    if (isConfigured && db) {
      const ref = doc(db, parentPath, docId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data();
      if (data.isDeleted) return null;
      return { id: snap.id, ...data };
    }

    const key = 'wc_sub_' + parentPath.replace(/\//g, '__');
    const all = JSON.parse(localStorage.getItem(key) || '[]');
    return all.find(d => d.id === docId && !d.isDeleted) || null;
  },

  // ── TMS entry points ───────────────────────────────────────────────
  // Thin wrappers so callers have a single adapter for TMS operations.

  /**
   * TMS-2: Carrier invoice receipt + three-way match.
   * Writes the invoice, runs the three-way match, and opens a dispute
   * when variance exceeds tolerance. Delegates to FreightAuditService
   * so all AP-matching logic stays in one place.
   */
  async receiveCarrierInvoice(invoiceData, userId = 'system') {
    const { default: FreightAuditService } = await import('./FreightAuditService');
    return FreightAuditService.receiveInvoice(invoiceData, userId);
  },

  // ── DEMO-2: Autonomous Prospect Demo ──────────────────────────────────

  /**
   * Subscribe to demo_engagements in real time for the sales dashboard.
   * Scoped to DEMO_TENANT_ID. Returns unsubscribe function.
   */
  subscribeToDemoEngagements(callback, maxEvents = 200) {
    const DEMO_TENANT = DEMO_TENANT_ID;
    if (isConfigured && db) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      const q = query(
        collection(db, 'demo_engagements'),
        where('tenantId', '==', DEMO_TENANT),
        where('timestamp', '>=', cutoff.toISOString()),
        orderBy('timestamp', 'desc'),
        limit(maxEvents),
      );
      return onSnapshot(q, (snap) => {
        callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }, (err) => {
        console.error('DataService.subscribeToDemoEngagements error:', err.message);
      });
    }
    // localStorage fallback
    const poll = () => {
      try {
        const all = JSON.parse(localStorage.getItem('wc_demo_engagements') || '[]');
        callback(all.slice(0, maxEvents));
      } catch { /* non-fatal */ }
    };
    poll();
    const timer = setInterval(poll, 4000);
    return () => clearInterval(timer);
  },

  /**
   * Query demo_sessions_prospect for the RECENT SESSIONS table.
   */
  async queryDemoSessions(daysBack = 30) {
    const DEMO_TENANT = DEMO_TENANT_ID;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - daysBack);

    if (isConfigured && db) {
      const q = query(
        collection(db, 'demo_sessions_prospect'),
        where('tenantId', '==', DEMO_TENANT),
        where('startedAt', '>=', cutoff.toISOString()),
        orderBy('startedAt', 'desc'),
        limit(100),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }

    try {
      const all = JSON.parse(localStorage.getItem('wc_demo_sessions_prospect') || '[]');
      return all.filter(s => s.tenantId === DEMO_TENANT);
    } catch { return []; }
  },

  /**
   * Query prospect_demos for all invitations.
   */
  async queryProspectDemos() {
    const DEMO_TENANT = DEMO_TENANT_ID;
    if (isConfigured && db) {
      const q = query(
        collection(db, 'prospect_demos'),
        where('tenantId', '==', DEMO_TENANT),
        orderBy('createdAt', 'desc'),
        limit(200),
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }
    try { return JSON.parse(localStorage.getItem('wc_prospect_demos') || '[]'); }
    catch { return []; }
  },

  /**
   * WORKSPACES-EMPTY-STATE-1 — return the per-role starter cards for a
   * tenant. Reads tenant-scoped overrides at
   *   tenants/{tenantId}/workspace_starters/{role}
   * If no override exists, falls back to the canonical starter set.
   *
   * The canonical set is imported lazily so this method works in
   * non-Firebase contexts (test, demo, localStorage fallback).
   */
  async getWorkspaceStarters(tenantId, role) {
    if (!role) return [];
    let canonical = [];
    try {
      const mod = await import('../customer/workspaces/workspaceStarters');
      canonical = mod.getCanonicalStarters(role) || [];
    } catch { canonical = []; }

    const tid = normalizeTenantId(tenantId) || this.getCurrentTenantId();
    if (isConfigured && db) {
      try {
        const ref = doc(db, `tenants/${tid}/workspace_starters/${role}`);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data?.starters) && data.starters.length > 0) {
            return data.starters;
          }
        }
      } catch {
        // fall through to canonical
      }
    }
    return canonical;
  },

  // ── SAVED-VIEWS-1 ─────────────────────────────────────────────────────────

  async getSavedViews(tenantId, userId, surfaceId) {
    const tid = normalizeTenantId(tenantId);
    if (isConfigured && db) {
      const constraints = [
        where('tenantId', '==', tid),
        where('surfaceId', '==', surfaceId),
        where('isDeleted', '==', false),
      ];
      const q = query(collection(db, 'saved_views'), ...constraints, orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      return all.filter(v =>
        v.ownerId === userId ||
        v.visibility === 'tenant' ||
        (Array.isArray(v.sharedWith) && v.sharedWith.includes(userId))
      );
    }
    const all = JSON.parse(localStorage.getItem('wc_saved_views') || '[]');
    return all.filter(v =>
      !v.isDeleted &&
      normalizeTenantId(v.tenantId) === tid &&
      v.surfaceId === surfaceId &&
      (v.ownerId === userId || v.visibility === 'tenant' ||
        (Array.isArray(v.sharedWith) && v.sharedWith.includes(userId)))
    );
  },

  async saveView(tenantId, userId, view) {
    const tid = normalizeTenantId(tenantId);
    const doc = {
      tenantId: tid,
      ownerId: userId,
      surfaceId: view.surfaceId,
      name: view.name,
      description: view.description ?? '',
      filters: view.filters ?? [],
      visibility: view.visibility ?? 'private',
      sharedWith: view.sharedWith ?? [],
      isDefault: view.isDefault ?? false,
    };
    return this.create('saved_views', doc, userId, 'saved-views-1');
  },

  async updateView(tenantId, viewId, patch, userId) {
    void tenantId;
    return this.update('saved_views', viewId, patch, userId);
  },

  async deleteView(tenantId, viewId, userId) {
    void tenantId;
    return this.delete('saved_views', viewId, userId);
  },

  async shareView(tenantId, viewId, shareWith, userId) {
    void tenantId;
    if (shareWith === 'tenant') {
      return this.update('saved_views', viewId, { visibility: 'tenant', sharedWith: [] }, userId);
    }
    if (shareWith === 'private') {
      return this.update('saved_views', viewId, { visibility: 'private', sharedWith: [] }, userId);
    }
    // shareWith is an array of userIds
    return this.update('saved_views', viewId, {
      visibility: 'shared',
      sharedWith: Array.isArray(shareWith) ? shareWith : [shareWith],
    }, userId);
  },

  async getViewById(tenantId, viewId) {
    const tid = normalizeTenantId(tenantId);
    if (isConfigured && db) {
      const ref = doc(db, 'saved_views', viewId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      const data = snap.data();
      if (normalizeTenantId(data.tenantId) !== tid) return null;
      return { id: snap.id, ...data };
    }
    const all = JSON.parse(localStorage.getItem('wc_saved_views') || '[]');
    const found = all.find(v => v.id === viewId && normalizeTenantId(v.tenantId) === tid);
    return found ?? null;
  },

  // ── TMS-CT-2-SEAT-F: Zone 9 Continuous Improvement Pulse ─────────────────

  async getAgentCalibrationRuns({ tenantId, weekStart } = {}) {
    const conditions = [
      { field: 'tenantId', op: '==', value: tenantId },
      { field: 'softDeleted', op: '==', value: false },
    ];
    if (weekStart) conditions.push({ field: 'weekStart', op: '>=', value: weekStart });
    return this.queryFlex('agent_calibration_runs', conditions, { orderBy: 'weekStart', direction: 'desc' });
  },

  subscribeAgentCalibrationRuns({ tenantId, onUpdate, onError }) {
    return this.subscribeRealtime({
      collection: 'agent_calibration_runs',
      where: [
        ['tenantId', '==', tenantId],
        ['softDeleted', '==', false],
      ],
      tenantId,
      onUpdate,
      onError,
    });
  },
};

void deleteField;

// ── TYPES-1: Validation helpers ───────────────────────────────────────────────

export class DataValidationError extends Error {
  constructor(schemaName, reasonCode, issues) {
    super(`DataService write blocked by schema validation [${schemaName}] — ${reasonCode}`);
    this.name = 'DataValidationError';
    this.schemaName = schemaName;
    this.reasonCode = reasonCode;
    this.issues = issues.map(({ code, message, path }) => ({ code, message, path }));
  }
}

function _zodReasonCode(zodError) {
  const first = zodError?.issues?.[0];
  if (!first) return 'VALIDATION_ERROR';
  return `INVALID_FIELD:${first.path.join('.') || 'root'}`;
}

// OBS-1: module-level structured logger + write/validation telemetry helpers.
const log = createLogger('DataService');

/**
 * Schema validation gate for DataService writes (TYPES-1 §3.1.7).
 * Uses .partial() for updates so unknown fields are allowed.
 * Emits telemetry on failure — never silently swallows violations.
 * Throws a structured error that callers surface to the user.
 */
function _validateWrite(collectionName, data, op) {
  const schema = SCHEMA_REGISTRY[collectionName];
  if (!schema) return; // no schema registered — allow write, WARN logged below
  const candidate = op === 'update' ? schema.partial() : schema;
  const result = candidate.safeParse(data);
  if (!result.success) {
    _emitValidationTelemetry({
      tenantId: data?.tenantId,
      callerModule: 'DataService',
      schemaName: collectionName,
      reasonCode: result.error.issues.map(i => i.code).join(','),
    });
    const issues = result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`).join('; ');
    throw new Error(`DataService.${op}(${collectionName}): schema validation failed — ${issues}`);
  }
}

/**
 * Per-tenant concurrent listener guard for subscribeRealtime.
 * Default ceiling: 10 000 (v14.8 §3.1.6 acceptance gate).
 * Override via DataService._realtimeGuard.ceiling for testing.
 */
const _realtimeGuard = (() => {
  const counts = new Map();
  const ceiling = 10_000;
  return {
    acquire(tenantId) {
      const n = (counts.get(tenantId) || 0) + 1;
      if (n > ceiling) {
        throw new Error(`subscribeRealtime: per-tenant listener ceiling (${ceiling}) reached for tenant ${tenantId}`);
      }
      counts.set(tenantId, n);
    },
    release(tenantId) {
      const n = Math.max(0, (counts.get(tenantId) || 1) - 1);
      counts.set(tenantId, n);
    },
    count(tenantId) { return counts.get(tenantId) || 0; },
    ceiling,
  };
})();

/**
 * Emit a structured ds-write entry on every Firestore write.
 * Never logs payload content — collection, op, tenantId, and correlationId
 * (auto-attached from context) only.
 */
function _emitWriteTelemetry(collection, op, tenantId, extra) {
  const payload = { collection, op };
  if (tenantId != null) payload.tenantId = tenantId;
  if (extra) Object.assign(payload, extra);
  log.info('ds-write', payload);
}

/**
 * OBS-1 consumer of TYPES-1's onValidationFailure signal.
 * Emits a structured warn entry; also forwards via EventBus for other listeners.
 * Never contains raw payload content (PII safety).
 */
function _emitValidationTelemetry({ tenantId, callerModule, schemaName, reasonCode }) {
  log.warn('ds-validation-failure', { tenantId, callerModule, schemaName, reasonCode });
  // Non-blocking fire: import EventBusService dynamically to avoid circular deps.
  import('./EventBusService.js').then(({ default: EventBus }) => {
    EventBus.emit('validation.failure', { tenantId, callerModule, schemaName, reasonCode }).catch(() => {});
  }).catch(() => {});
}

// ── ARTEMIS-0 composite-read namespace ──────────────────────────────────────
// CRM + ARTEMIS enrichment fused at read time. UI components call
//   DataService.composite.contact(id) → { ...crmFields, artemis: {...} }
// and never assemble the join themselves. Uses dynamic import so the CRM
// service graph stays lazy-loaded and ARTEMIS services inherit the same
// no-direct-Firestore discipline enforced by ESLint.
async function _artemis() {
  const mod = await import('./artemis/ArtemisEnrichmentService.js');
  return mod.default;
}

async function _crm() {
  const mod = await import('./CRMService.js');
  return mod.default;
}

async function buildComposite(entity, crmFetcher, entityId, tenantId) {
  const [crm, artemis] = await Promise.all([
    Promise.resolve()
      .then(crmFetcher)
      .catch(() => null),
    (async () => {
      try {
        const svc = await _artemis();
        return await svc.getEnrichment(entity, tenantId || DataService.getCurrentTenantId(), entityId);
      } catch { return {}; }
    })(),
  ]);
  if (!crm) return { id: entityId, artemis };
  return { ...crm, artemis };
}

DataService.composite = {
  async contact(contactId, tenantId) {
    return buildComposite('contact', async () => {
      const crm = await _crm();
      return crm.getContact(contactId);
    }, contactId, tenantId);
  },
  async account(accountId, tenantId) {
    return buildComposite('account', async () => {
      const crm = await _crm();
      return crm.getAccount(accountId);
    }, accountId, tenantId);
  },
  async opportunity(dealId, tenantId) {
    return buildComposite('opportunity', async () => {
      const crm = await _crm();
      const all = await crm.listDeals();
      return all.find(d => d.id === dealId) || null;
    }, dealId, tenantId);
  },
  async activity(activityId, tenantId) {
    return buildComposite('activity', async () => {
      const crm = await _crm();
      const all = await crm.listActivities();
      return all.find(a => a.id === activityId) || null;
    }, activityId, tenantId);
  },
};

// ── NAV-1-DENSITY-FIRST-RUN-1 — Density preference + telemetry methods ────
//
// Authority: dd-decision-006 (DENSITY-OVERRIDE-DELIBERATION-1) + BRAND-BOOK
// §XV §5.5. Soft-default contract: manifest seeds the user's first session;
// once a preference exists, user wins permanently.
//
// Storage layout (canonical SETTINGS-HUB-1a path; sibling fields under
// `interface`, additive to the existing single-string `density`):
//   user_preferences/{uid}.interface.density           — mode (string)
//   user_preferences/{uid}.interface.densityArchetype  — archetype (string)
//   user_preferences/{uid}.interface.densitySeededAt   — ISO timestamp
//
// Module manifest registry is a build-time artefact (not Firestore):
//   src/generated/module-manifest-registry.json
//
// Telemetry writes go to top-level `telemetry_events` (90d retention) and
// the aggregateDensityTelemetry Cloud Function rolls them up.
//
// PII: userId is sha-256 hashed at the call site of emitDensityTelemetry;
// raw uid never persists in telemetry_events (per EXEC-50 ethics framework).

const DENSITY_FALLBACK_ARCHETYPE = 'hybrid';
const DENSITY_FALLBACK_MODE      = 'comfortable';

async function _hashUserId(uid) {
  // Stable SHA-256 hash of `uid`. Used for telemetry payload only; never
  // for indexing user records. Browser path uses SubtleCrypto; a fallback
  // (e.g., emulator/SSR) returns a non-cryptographic FNV-1a so writes do
  // not block — telemetry is best-effort, not auth.
  const text = String(uid || '');
  try {
    if (typeof crypto !== 'undefined' && crypto?.subtle) {
      const buf = new TextEncoder().encode(text);
      const digest = await crypto.subtle.digest('SHA-256', buf);
      return Array.from(new Uint8Array(digest))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
    }
  } catch { /* fall through */ }
  // FNV-1a fallback (non-cryptographic; only used when SubtleCrypto absent).
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return ('00000000' + (h >>> 0).toString(16)).slice(-8);
}

function _surfaceClassForArchetype(archetype) {
  if (archetype === 'bloomberg') return 'operational';
  if (archetype === 'linear')    return 'calm';
  return 'standard'; // hybrid OR null fallback
}

let _manifestRegistryPromise = null;
async function _loadManifestRegistry() {
  if (!_manifestRegistryPromise) {
    _manifestRegistryPromise = import('../generated/module-manifest-registry.json')
      .then(mod => mod.default || mod)
      .catch(() => ({ modules: {} }));
  }
  return _manifestRegistryPromise;
}

async function _prefsService() {
  const mod = await import('./PreferencesService.js');
  return mod.default;
}

/**
 * Read a module manifest's density fields from the bundled registry.
 * Returns null when the module is non-surface OR not found — both cases
 * trigger the seeding fallback (hybrid + comfortable).
 *
 * @param {string} moduleId
 * @returns {Promise<{archetype: string, mode: string} | null>}
 */
DataService.getModuleManifestDensity = async function getModuleManifestDensity(moduleId) {
  if (!moduleId) return null;
  const registry = await _loadManifestRegistry();
  const entry = registry?.modules?.[moduleId];
  if (!entry || entry.isSurface === false) return null;
  if (!entry.densityArchetype || !entry.densityMode) return null;
  return { archetype: entry.densityArchetype, mode: entry.densityMode };
};

/**
 * Read user's persisted density preference. Reads via PreferencesService
 * (canonical SETTINGS-HUB-1a path).
 *
 * Returns null if no preference set yet (first-run case — caller seeds).
 * Returns { archetype, mode, seededAt } when set.
 *
 * Legacy schema migration (per dd-decision-006 operator verdict 2026-05-04):
 *   When `interface.density` exists as a string but `densityArchetype` or
 *   `densitySeededAt` is missing, this function:
 *     (a) Treats the string as `mode` (existing values are canonical mode vocab).
 *     (b) Derives archetype from view_mode_primary_module manifest lookup;
 *         falls back to 'hybrid' when primary module unset OR non-surface.
 *     (c) Sets seededAt to current timestamp (legacy backfill semantics).
 *     (d) Writes back via setUserDensityPreference(..., {isFirstRunSeed:false}).
 *     (e) Returns the new shape to caller.
 *   No data loss; no user-visible value change.
 */
DataService.getUserDensityPreference = async function getUserDensityPreference(tenantId, userId) {
  if (!tenantId || !userId) return null;
  const prefs = await _prefsService();
  const doc = await prefs.get(userId, tenantId);
  const iface = doc?.interface || {};
  const mode      = iface.density;
  const archetype = iface.densityArchetype;
  const seededAt  = iface.densitySeededAt;

  // No preference at all → first-run case.
  if (!mode && !archetype) return null;

  // Full new-shape preference present → return it.
  if (mode && archetype && seededAt) {
    return { archetype, mode, seededAt };
  }

  // Legacy migration: string-only `density` (or partial fields).
  // Derive archetype from view_mode_primary_module if available.
  let derivedArchetype = archetype || DENSITY_FALLBACK_ARCHETYPE;
  if (!archetype) {
    try {
      const navPref = await DataService.get(
        'nav_user_preferences',
        `${tenantId}_${userId}`,
      );
      const primaryModuleId = navPref?.view_mode_primary_module;
      if (primaryModuleId) {
        const manifest = await DataService.getModuleManifestDensity(primaryModuleId);
        if (manifest?.archetype) derivedArchetype = manifest.archetype;
      }
    } catch { /* fall back to DENSITY_FALLBACK_ARCHETYPE */ }
  }

  const derivedMode = mode || DENSITY_FALLBACK_MODE;
  const derivedSeededAt = seededAt || new Date().toISOString();

  // Backfill the new shape (not first-run; preserve user's existing mode).
  try {
    await DataService.setUserDensityPreference(
      tenantId,
      userId,
      derivedArchetype,
      derivedMode,
      { isFirstRunSeed: false, _legacyBackfill: true, _seededAt: derivedSeededAt },
    );
  } catch {
    // Backfill is best-effort. Subsequent reads will retry.
  }

  return { archetype: derivedArchetype, mode: derivedMode, seededAt: derivedSeededAt };
};

/**
 * Write user's density preference via PreferencesService (canonical path).
 *
 * Sets densitySeededAt to now() when not already set (covers both the
 * first-run seed and the legacy backfill); preserves the original
 * timestamp on subsequent toggles so analytics can distinguish
 * seeded-vs-toggled cohorts.
 *
 * @param {string} tenantId
 * @param {string} userId
 * @param {string} archetype  bloomberg | hybrid | linear
 * @param {string} mode       compact | comfortable | spacious
 * @param {object} opts
 * @param {boolean} opts.isFirstRunSeed
 * @param {string=} opts._seededAt  internal: pin seededAt (legacy backfill)
 */
DataService.setUserDensityPreference = async function setUserDensityPreference(
  tenantId, userId, archetype, mode, opts = {},
) {
  if (!tenantId || !userId) throw new Error('setUserDensityPreference: tenantId + userId required');
  const prefs = await _prefsService();

  // Determine seededAt:
  //   - opts._seededAt (legacy backfill) wins if provided.
  //   - existing densitySeededAt preserved on subsequent toggles.
  //   - else now().
  let seededAt = opts._seededAt;
  if (!seededAt) {
    const existing = await prefs.get(userId, tenantId);
    seededAt = existing?.interface?.densitySeededAt || new Date().toISOString();
  }

  await prefs.patch(userId, tenantId, {
    interface: {
      density:           mode,
      densityArchetype:  archetype,
      densitySeededAt:   seededAt,
    },
  }, userId);

  _emitWriteTelemetry('user_preferences', 'density-set', tenantId, {
    isFirstRunSeed: !!opts.isFirstRunSeed,
  });

  return { archetype, mode, seededAt };
};

/**
 * Emit a `density.toggle` event to the telemetry_events collection.
 * Best-effort — failures are swallowed so a flaky telemetry path never
 * blocks a user's toggle action.
 *
 * Caller is responsible for hashing userId before passing — though for
 * convenience this method also accepts a raw `userId` field and will hash
 * it internally if `userIdHash` is not supplied.
 *
 * @param {object} payload
 * @param {string} payload.tenantId
 * @param {string=} payload.userId       raw uid (will be hashed)
 * @param {string=} payload.userIdHash   pre-hashed (preferred)
 * @param {string|null} payload.fromArchetype
 * @param {string|null} payload.fromMode
 * @param {string} payload.toArchetype
 * @param {string} payload.toMode
 * @param {string=} payload.surfaceClass derived from toArchetype if absent
 * @param {string=} payload.timestamp    defaults to now()
 */
DataService.emitDensityTelemetry = async function emitDensityTelemetry(payload = {}) {
  try {
    const userIdHash = payload.userIdHash
      || (payload.userId ? await _hashUserId(payload.userId) : null);
    if (!payload.tenantId || !userIdHash) return;

    const event = {
      eventType:     'density.toggle',
      tenantId:      payload.tenantId,
      userIdHash,
      fromArchetype: payload.fromArchetype ?? null,
      fromMode:      payload.fromMode ?? null,
      toArchetype:   payload.toArchetype,
      toMode:        payload.toMode,
      surfaceClass:  payload.surfaceClass || _surfaceClassForArchetype(payload.toArchetype),
      timestamp:     payload.timestamp || new Date().toISOString(),
    };

    await DataService.create('telemetry_events', event, 'system', 'density-telemetry');
  } catch {
    // Telemetry is best-effort. Never block the toggle.
  }
};

DataService.contacts = {
  async getComposite(contactId, tenantId) { return DataService.composite.contact(contactId, tenantId); },
};
DataService.accounts = {
  async getComposite(accountId, tenantId) { return DataService.composite.account(accountId, tenantId); },
};
DataService.opportunities = {
  async getComposite(dealId, tenantId) { return DataService.composite.opportunity(dealId, tenantId); },
};
DataService.activities = {
  async getComposite(activityId, tenantId) { return DataService.composite.activity(activityId, tenantId); },
};

// ── SHARE-PRIMITIVE-1 (SEAT-131) ────────────────────────────────────────────
// Firestore collection: shared_links/{tenantId}/links/{shareToken}
// RBAC enforced at access time by resolveSharedLink Cloud Function.
// Every share creation and access is audited via KAUDIT-7.

DataService.createShare = async function createShare(tenantId, recordType, recordId, accessConfig) {
  const shareToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

  const effectiveTenant = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
  const subPath = `shared_links/${effectiveTenant}/links`;

  const shareDoc = {
    shareToken,
    recordType,
    recordId,
    tenantId: effectiveTenant,
    recipientType: accessConfig.recipientType,
    recipientId: accessConfig.recipientId ?? null,
    recipientEmail: accessConfig.recipientEmail ?? null,
    // public/link shares are capped at view access
    accessLevel: accessConfig.recipientType === 'link' ? 'view' : (accessConfig.accessLevel ?? 'view'),
    expiresAt: accessConfig.expiresAt ?? null,
    revokedAt: null,
  };

  const created = await DataService.createSub(subPath, shareDoc, 'system', 'share-primitive-1');
  return created;
};

DataService.revokeShare = async function revokeShare(tenantId, shareId) {
  const effectiveTenant = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
  const subPath = `shared_links/${effectiveTenant}/links`;
  await DataService.updateSub(subPath, shareId, { revokedAt: new Date().toISOString() }, 'system');
};

DataService.listShares = async function listShares(tenantId, recordType, recordId) {
  const effectiveTenant = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
  const subPath = `shared_links/${effectiveTenant}/links`;
  // querySub returns isDeleted===false records; filter by record in memory
  const all = await DataService.querySub(subPath);
  return all.filter(s => s.recordType === recordType && s.recordId === recordId);
};

// ── WHY-BUTTON-1 (SEAT-131) — KAUDIT trace surface ──────────────────────────
// Reads aegis_reasoning_chains/{tenantId}/traces/{traceId} written by governedAegisCall.
// RBAC: trace must belong to the caller's tenant. Never returns other-tenant traces.

DataService.getKauditTrace = async function getKauditTrace(tenantId, traceId) {
  const effectiveTenant = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
  const subPath = `aegis_reasoning_chains/${effectiveTenant}/traces`;
  const trace = await DataService.getSubById(subPath, traceId);

  if (!trace) return null;
  // RBAC hard stop — never surface another tenant's trace
  if (trace.tenantId && normalizeTenantId(trace.tenantId) !== effectiveTenant) {
    throw new Error('getKauditTrace: tenant isolation violation');
  }
  return trace;
};

// ── AOP-FOUNDATION-1 / AGENT-BUILDER-1 (SEAT-113) ───────────────────────────

DataService.aop = {
  /** Returns all registered AOP procedures available to the tenant. */
  async getAvailableProcedures(tenantId) {
    const tid = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
    return DataService.query('aop_procedures', [
      { field: 'tenantId', op: '==', value: tid },
      { field: 'deletedAt', op: '==', value: null },
    ]);
  },

  /** Persists a composed agent definition for the tenant. */
  async saveAgentDefinition(tenantId, agentSpec) {
    const tid = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
    if (!agentSpec?.name || !agentSpec?.steps) {
      throw new Error('DataService.aop.saveAgentDefinition: agentSpec must include name and steps');
    }
    return DataService.create('agent_definitions', { ...agentSpec, tenantId: tid });
  },

  /** Queues a test execution run for a composed agent against a fixture set. */
  async testAgentRun(tenantId, agentSpec, fixture) {
    const tid = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
    return DataService.create('agent_test_runs', {
      tenantId: tid,
      agentSpec,
      fixture: fixture ?? {},
      status: 'queued',
      startedAt: new Date().toISOString(),
    });
  },
};

// Exposed for testing and monitoring (read-only access to listener counts).
DataService._realtimeGuard = _realtimeGuard;

// ── IOT-HAL-1: Hardware Abstraction Layer Firestore methods ───────────────────
// All IoT collections are tenantId-denormalized. Soft-delete only (never hard delete).

DataService.hal = {

  /**
   * Read a device record from iot_devices.
   *
   * @param {string} deviceId
   * @returns {Promise<object>}
   */
  async getDevice(deviceId) {
    return DataService.get('iot_devices', deviceId);
  },

  /**
   * Write or update a device record in iot_devices.
   * Idempotent by deviceId — upsert semantics.
   *
   * @param {object} deviceData  must include tenantId, vendor, model
   * @returns {Promise<object>}
   */
  async upsertDevice(deviceData) {
    const tid = normalizeTenantId(deviceData.tenantId) || DataService.getCurrentTenantId();
    if (!tid) throw new Error('DataService.hal.upsertDevice: tenantId required');
    if (deviceData.id) {
      await DataService.update('iot_devices', deviceData.id, { ...deviceData, tenantId: tid });
      return { id: deviceData.id, ...deviceData, tenantId: tid };
    }
    return DataService.create('iot_devices', { ...deviceData, tenantId: tid });
  },

  /**
   * Write a normalized telemetry reading to iot_readings.
   * Idempotent by vendorMessageId when provided.
   *
   * @param {import('./iot/contracts/DeviceContract.ts').NormalizedReading} reading
   * @param {string} [vendorMessageId]  unique vendor message ID for idempotency
   * @returns {Promise<object>}
   */
  async writeReading(reading, vendorMessageId) {
    const tid = normalizeTenantId(reading.tenantId) || DataService.getCurrentTenantId();
    const doc = {
      ...reading,
      tenantId: tid,
      softDeleted: false,
      ...(vendorMessageId && { vendorMessageId }),
    };
    // Idempotency: if vendorMessageId exists, check for duplicate before writing
    if (vendorMessageId) {
      const existing = await DataService.queryFlex('iot_readings', [
        { field: 'tenantId',        op: '==', value: tid },
        { field: 'vendorMessageId', op: '==', value: vendorMessageId },
        { field: 'softDeleted',     op: '==', value: false }, // IOT-GDPR-CCPA-COMPLIANCE-1
      ]);
      if (existing.length > 0) return existing[0];
    }
    return DataService.create('iot_readings', doc);
  },

  /**
   * Write a canonical device event to iot_events.
   *
   * @param {import('./iot/contracts/DeviceContract.ts').DeviceEvent} event
   * @returns {Promise<object>}
   */
  async writeEvent(event) {
    const tid = normalizeTenantId(event.tenantId) || DataService.getCurrentTenantId();
    return DataService.create('iot_events', {
      ...event,
      tenantId:    tid,
      softDeleted: false,
    });
  },

  /**
   * Write or update the current health state for a device.
   *
   * @param {import('./iot/contracts/DeviceContract.ts').DeviceHealth} health
   * @returns {Promise<void>}
   */
  async upsertHealth(health) {
    const tid = normalizeTenantId(
      DataService.getCurrentTenantId ? DataService.getCurrentTenantId() : null
    );
    if (isConfigured && db) {
      const { doc: firestoreDoc, setDoc: firestoreSetDoc, serverTimestamp: firestoreServerTimestamp } =
        await import('firebase/firestore').catch(() => ({}));
      if (firestoreDoc && firestoreSetDoc) {
        const ref = firestoreDoc(db, 'iot_devices', health.deviceId, 'health_current');
        await firestoreSetDoc(ref, { ...health, updatedAt: firestoreServerTimestamp() }, { merge: true });
        return;
      }
    }
    // localStorage fallback
    const key = `wc_iot_health_${health.deviceId}`;
    localStorage.setItem(key, JSON.stringify({ ...health, updatedAt: new Date().toISOString() }));
  },

  /**
   * Write an entry to the iot_config_push_log collection (append-only audit).
   *
   * @param {object} logEntry  { tenantId, deviceId, delta, status, pushedAt, actor, receipt }
   * @returns {Promise<object>}
   */
  async writeConfigPushLog(logEntry) {
    const tid = normalizeTenantId(logEntry.tenantId) || DataService.getCurrentTenantId();
    return DataService.create('iot_config_push_log', {
      ...logEntry,
      tenantId:    tid,
      softDeleted: false,
    });
  },

  /**
   * Query device records for a tenant, optionally filtered by vendor.
   *
   * @param {string} tenantId
   * @param {{ vendor?: string, lifecycleState?: string }} [filters]
   * @returns {Promise<object[]>}
   */
  async listDevices(tenantId, filters = {}) {
    const tid = normalizeTenantId(tenantId) || DataService.getCurrentTenantId();
    const conditions = [
      { field: 'tenantId',    op: '==',    value: tid },
      { field: 'softDeleted', op: '==',    value: false },
    ];
    if (filters.vendor) {
      conditions.push({ field: 'vendor', op: '==', value: filters.vendor });
    }
    if (filters.lifecycleState) {
      conditions.push({ field: 'lifecycleState', op: '==', value: filters.lifecycleState });
    }
    return DataService.queryFlex('iot_devices', conditions); // IOT-GDPR-CCPA-COMPLIANCE-1
  },

  /**
   * Soft-delete a device record. Never hard-delete per house rules.
   *
   * @param {string} deviceId
   * @param {string} reason
   * @returns {Promise<void>}
   */
  async softDeleteDevice(deviceId, reason) {
    await DataService.update('iot_devices', deviceId, {
      softDeleted: true,
      deletedAt:   new Date().toISOString(),
      deleteReason: reason,
    });
  },

  // ── IOT-DEVICE-CONFIG-SCREEN-1 Amendment-1 §2 — agenticConfigProxy wrappers ─
  // These delegate to src/services/iot/__internal__/agenticConfigProxy.js so the
  // UI calls only DataService.hal — never HalService or substrate services
  // directly. FOUNDATION-PURITY-1 (AC-5) compliance.
  //
  // Dynamic import avoids the circular dependency that would occur if the proxy
  // (which imports DataService) were statically imported at module load.

  /**
   * Operator authors a config delta directly. See Amendment-1 §2.1.
   * @param {{ tenantId: string, deviceId: string, delta: object, actor: object }} params
   * @returns {Promise<{ receiptId: string, outcome: 'applied'|'failed', error?: string }>}
   */
  async submitHumanConfig(params) {
    const { submitHumanConfig } = await import('./iot/__internal__/agenticConfigProxy.js');
    return submitHumanConfig(params);
  },

  /**
   * Operator accepts an AEGIS HITL proposal. See Amendment-1 §2.2.
   * @param {{ receiptId: string, actor: object }} params
   * @returns {Promise<{ receiptId: string, outcome: 'applied'|'failed'|'stale', currentStatus?: string, error?: string }>}
   */
  async acceptPendingProposal(params) {
    const { acceptPendingProposal } = await import('./iot/__internal__/agenticConfigProxy.js');
    return acceptPendingProposal(params);
  },

  /**
   * Operator rejects an AEGIS HITL proposal. See Amendment-1 §2.3.
   * @param {{ receiptId: string, actor: object, reason?: string|null }} params
   * @returns {Promise<{ receiptId: string, outcome: 'rejected'|'stale', currentStatus?: string }>}
   */
  async rejectPendingProposal(params) {
    const { rejectPendingProposal } = await import('./iot/__internal__/agenticConfigProxy.js');
    return rejectPendingProposal(params);
  },

  /**
   * Operator modifies an AEGIS proposal and submits the modified version.
   * Produces a fresh human-originated receipt per IDM §4.7. See Amendment-1 §2.4.
   * @param {{ receiptId: string, modifiedDelta: object, actor: object }} params
   * @returns {Promise<{ originalReceiptId: string, newReceiptId?: string, outcome: 'applied'|'failed'|'stale', currentStatus?: string, error?: string }>}
   */
  async modifyPendingProposal(params) {
    const { modifyPendingProposal } = await import('./iot/__internal__/agenticConfigProxy.js');
    return modifyPendingProposal(params);
  },

  /**
   * Query PENDING-status receipts. UI calls this to render the AEGIS banner.
   * See Amendment-1 §2.5.
   * @param {{ tenantId: string, deviceId?: string, since?: string }} params
   * @returns {Promise<object[]>}
   */
  async listPendingProposals(params) {
    const { listPendingProposals } = await import('./iot/__internal__/agenticConfigProxy.js');
    return listPendingProposals(params);
  },

  /**
   * Operator-initiated AEGIS optimize. Delegates to AgenticConfigOrchestrator
   * via the proxy so UI never imports the orchestrator directly. Added as
   * part of the FOUNDATION-PURITY-1 cleanup that accompanies Amendment-1 —
   * the doctrine root cause identifies UI imports of AgenticConfigOrchestrator
   * as a violation to be removed by the takeover.
   *
   * @param {object} params  { tenantId, deviceId, actor, cargoDescriptor, customerDescriptor, routeDescriptor, carrierDescriptor, weatherContext?, tenantConstraints?, correlationId? }
   * @returns {Promise<{ outcome: 'applied'|'failed'|'hitl_required'|'abstained', receiptId: string, proposal?: object, floor?: number }>}
   */
  async triggerAegisOptimize(params) {
    const { triggerAegisOptimize } = await import('./iot/__internal__/agenticConfigProxy.js');
    return triggerAegisOptimize(params);
  },

  // ── IOT-2-DIGITAL-TWIN-1: digital twin proxy wrappers ──────────────────────
  twin: {
    async getAssetTwin(params) {
      const { getAssetTwin } = await import('./iot/__internal__/twinProxy.js');
      return getAssetTwin(params);
    },
    subscribeAssetTwin(params) {
      return import('./iot/__internal__/twinProxy.js').then(({ subscribeAssetTwin }) => subscribeAssetTwin(params));
    },
    async requestSimulation(params) {
      const { requestSimulation } = await import('./iot/__internal__/twinProxy.js');
      return requestSimulation(params);
    },
    async getMaintenanceRecommendation(params) {
      const { getMaintenanceRecommendation } = await import('./iot/__internal__/twinProxy.js');
      return getMaintenanceRecommendation(params);
    },
    async getDispatchAdvice(params) {
      const { getDispatchAdvice } = await import('./iot/__internal__/twinProxy.js');
      return getDispatchAdvice(params);
    },
  },

  // ── IOT-2-CORRELATION-1 §4 — correlationProxy wrappers ───────────────────
  // Dynamic import mirrors agenticConfigProxy pattern: avoids circular dep
  // (correlationProxy → CorrelationService → DataService).

  async getCorrelationScore(params) {
    const { correlationProxy } = await import('./iot/__internal__/correlationProxy.js');
    return correlationProxy.getCorrelationScore(params);
  },

  subscribeCorrelationScore({ tenantId, shipmentId, callback }) {
    // Inline EventBus subscription — avoids circular dep by lazy-loading EventBusService.
    // Same lazy-import pattern used elsewhere in DataService for EventBus access.
    let _unsub = null;
    import('./EventBusService.js').then(({ default: EventBus }) => {
      _unsub = EventBus.subscribe('iot.correlation.score.changed', (event) => {
        if (event.tenantId === tenantId && event.payload?.shipmentId === shipmentId) {
          callback(event.payload);
        }
      });
    });
    return () => { if (_unsub) _unsub(); };
  },

  async getPredictedBreach(params) {
    const { correlationProxy } = await import('./iot/__internal__/correlationProxy.js');
    return correlationProxy.getPredictedBreach(params);
  },

  async upsertCorrelationScore(scoreDoc) {
    const tid = scoreDoc.tenantId;
    if (!tid) throw new Error('DataService.hal.upsertCorrelationScore: tenantId required');
    if (scoreDoc.id) {
      await DataService.update('iot_correlation_scores', scoreDoc.id, scoreDoc);
      return { ...scoreDoc };
    }
    return DataService.create('iot_correlation_scores', scoreDoc);
  },
};

// ── IOT-FUSION-2 §4 — fleet correlation HAL write methods ────────────────────
// Write path stays inside DataService per HAL-WRITE-PATH-DOCTRINE-1.

DataService.hal.upsertFleetCorrelation = async function(doc) {
  const tid = doc.tenantId;
  if (!tid) throw new Error('DataService.hal.upsertFleetCorrelation: tenantId required');
  if (doc.id) {
    await DataService.update('iot_fleet_correlations', doc.id, doc);
    return { ...doc };
  }
  return DataService.create('iot_fleet_correlations', doc);
};

DataService.hal.listFleetCorrelations = async function({ tenantId, contextType, status, limit = 50 } = {}) {
  if (!tenantId) throw new Error('DataService.hal.listFleetCorrelations: tenantId required');
  const filters = [['tenantId', '==', tenantId]];
  if (contextType) filters.push(['contextType', '==', contextType]);
  if (status) filters.push(['status', '==', status]);
  return DataService.query('iot_fleet_correlations', filters, { limit, orderBy: 'detectedAt', orderDir: 'desc' });
};

DataService.hal.listAssetsForCorrelation = async function({ tenantId }) {
  if (!tenantId) throw new Error('DataService.hal.listAssetsForCorrelation: tenantId required');
  // Returns active shipment assets with latest readings + context identifiers.
  // Reads from iot_readings (most recent per device) joined with shipment context.
  return DataService.queryFlex('iot_readings', [
    { field: 'tenantId',    op: '==', value: tenantId },
    { field: 'status',      op: 'in',  value: ['in_transit', 'at_facility'] },
    { field: 'softDeleted', op: '==', value: false }, // IOT-GDPR-CCPA-COMPLIANCE-1
  ], { limit: 500 });
};

// ── IOT-2-CONNECTED-PRODUCTS-1 §4 — connectedProducts proxy (2 lines) ─────────
// Dynamic import avoids circular dependency (proxy imports DataService).

DataService.connectedProducts = {
  async getConnectedProductEvents(params) {
    const { getConnectedProductEvents } = await import('./iot/__internal__/connectedProductsProxy.js');
    return getConnectedProductEvents(params);
  },
  async assessCascadeImpact(params) {
    const { assessCascadeImpact } = await import('./iot/__internal__/connectedProductsProxy.js');
    return assessCascadeImpact(params);
  },
  async proposeCascade(params) {
    const { proposeCascade } = await import('./iot/__internal__/connectedProductsProxy.js');
    return proposeCascade(params);
  },
  async applyCascade(params) {
    const { applyCascade } = await import('./iot/__internal__/connectedProductsProxy.js');
    return applyCascade(params);
  },
  async rejectCascade(params) {
    const { rejectCascade } = await import('./iot/__internal__/connectedProductsProxy.js');
    return rejectCascade(params);
  },
  async modifyCascade(params) {
    const { modifyCascade } = await import('./iot/__internal__/connectedProductsProxy.js');
    return modifyCascade(params);
  },
};

// ── LOADBOARD-AGENT-1 ────────────────────────────────────────────────────────

DataService.createLoadBoardAgentLog = async function createLoadBoardAgentLog(tenantId, loadId, loadData) {
  if (!tenantId) throw new Error('DataService.createLoadBoardAgentLog: tenantId required');
  if (!loadId) throw new Error('DataService.createLoadBoardAgentLog: loadId required');
  const doc = await DataService.create('load_board_agent_log', {
    tenantId,
    loadId,
    status: 'private_network_searching',
    privateNetworkPostedAt: null,
    privateNetworkTimeoutAt: null,
    chromeAgentRequestedAt: null,
    loadBoardPostedAt: null,
    platforms: [],
    respondents: [],
    shortlist: [],
    selectedCarrierId: null,
    failureReason: null,
    loadData: loadData ?? null,
  });
  return doc.id;
};

DataService.updateLoadBoardAgentStatus = async function updateLoadBoardAgentStatus(tenantId, agentLogId, status, delta = {}) {
  if (!tenantId) throw new Error('DataService.updateLoadBoardAgentStatus: tenantId required');
  const allowed = [
    'private_network_searching', 'private_network_timeout', 'awaiting_chrome_agent',
    'load_board_posting', 'monitoring_responses', 'shortlist_ready',
    'carrier_selected', 'failed', 'cancelled',
  ];
  if (!allowed.includes(status)) throw new Error(`DataService.updateLoadBoardAgentStatus: invalid status '${status}'`);
  return DataService.update('load_board_agent_log', agentLogId, { tenantId, status, ...delta });
};

DataService.getCarrierPrivateNetwork = async function getCarrierPrivateNetwork(tenantId, originState, destState) {
  if (!tenantId) throw new Error('DataService.getCarrierPrivateNetwork: tenantId required');
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  const all = await DataService.queryFlex('carrier_private_network', [
    { field: 'tenantId', op: '==', value: tenantId },
    { field: 'lastUsedAt', op: '>=', value: thirtyDaysAgo },
  ]);
  if (!originState && !destState) return all;
  return all.filter(c =>
    Array.isArray(c.preferredLanes) &&
    c.preferredLanes.some(lane =>
      (!originState || lane.originState === originState) &&
      (!destState || lane.destState === destState)
    )
  );
};

DataService.writeShortlistToAgentLog = async function writeShortlistToAgentLog(tenantId, agentLogId, shortlist) {
  if (!tenantId) throw new Error('DataService.writeShortlistToAgentLog: tenantId required');
  return DataService.update('load_board_agent_log', agentLogId, {
    tenantId,
    shortlist,
    status: 'shortlist_ready',
  });
};

DataService.appendRespondentToAgentLog = async function appendRespondentToAgentLog(tenantId, agentLogId, respondent) {
  if (!tenantId) throw new Error('DataService.appendRespondentToAgentLog: tenantId required');
  const current = await DataService.getById('load_board_agent_log', agentLogId, tenantId);
  const respondents = Array.isArray(current?.respondents) ? current.respondents : [];
  return DataService.update('load_board_agent_log', agentLogId, {
    tenantId,
    respondents: [...respondents, respondent],
    status: 'monitoring_responses',
  });
};

// ── CARRIER-VETTING-WORLD-CLASS-1 ─────────────────────────────────────────
// All 9 DataService methods for the world-class carrier vetting pipeline.
// Collections: carrier_vetting_signals, carrier_phone_intel_cache,
//              carrier_bank_account_changes.
// G-19: All methods are tenantId-scoped. G-24: No hard deletes.

DataService.getCarrierVettingSignals = async function getCarrierVettingSignals(tenantId, carrierId) {
  if (!tenantId) throw new Error('DataService.getCarrierVettingSignals: tenantId required');
  if (!carrierId) throw new Error('DataService.getCarrierVettingSignals: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  }

  const all = JSON.parse(localStorage.getItem('wc_carrier_vetting_signals') || '[]');
  return all.find(d => d.id === docId && normalizeTenantId(d.tenantId) === tid) || null;
};

DataService.writeCarrierVettingSignals = async function writeCarrierVettingSignals(tenantId, carrierId, signals) {
  if (!tenantId) throw new Error('DataService.writeCarrierVettingSignals: tenantId required');
  if (!carrierId) throw new Error('DataService.writeCarrierVettingSignals: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  _emitWriteTelemetry('carrier_vetting_signals', 'upsert', tid);

  const payload = {
    ...signals,
    tenantId: tid,
    carrierId,
    updatedAt: serverTimestamp(),
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    await setDoc(ref, payload, { merge: true });
    return { id: docId, ...payload };
  }

  const key = 'wc_carrier_vetting_signals';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const record = { id: docId, ...payload, updatedAt: new Date().toISOString() };
  const idx = all.findIndex(d => d.id === docId);
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return record;
};

DataService.writePhoneIntelResult = async function writePhoneIntelResult(tenantId, carrierId, result) {
  if (!tenantId) throw new Error('DataService.writePhoneIntelResult: tenantId required');
  if (!carrierId) throw new Error('DataService.writePhoneIntelResult: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  _emitWriteTelemetry('carrier_vetting_signals', 'update', tid);

  const patch = {
    tenantId: tid,
    carrierId,
    'layers.phone': { ...result, lastCheckedAt: serverTimestamp() },
    updatedAt: serverTimestamp(),
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    await setDoc(ref, patch, { merge: true });
    return { id: docId, ...patch };
  }

  const key = 'wc_carrier_vetting_signals';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = all.findIndex(d => d.id === docId);
  const record = idx >= 0 ? { ...all[idx] } : { id: docId, tenantId: tid, carrierId, layers: {} };
  record.layers = { ...(record.layers || {}), phone: { ...result, lastCheckedAt: new Date().toISOString() } };
  record.updatedAt = new Date().toISOString();
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return record;
};

DataService.writeLinguisticRiskResult = async function writeLinguisticRiskResult(tenantId, carrierId, result) {
  if (!tenantId) throw new Error('DataService.writeLinguisticRiskResult: tenantId required');
  if (!carrierId) throw new Error('DataService.writeLinguisticRiskResult: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  _emitWriteTelemetry('carrier_vetting_signals', 'update', tid);

  const patch = {
    tenantId: tid,
    carrierId,
    'layers.linguistic': { ...result, lastCheckedAt: serverTimestamp() },
    updatedAt: serverTimestamp(),
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    await setDoc(ref, patch, { merge: true });
    return { id: docId, ...patch };
  }

  const key = 'wc_carrier_vetting_signals';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = all.findIndex(d => d.id === docId);
  const record = idx >= 0 ? { ...all[idx] } : { id: docId, tenantId: tid, carrierId, layers: {} };
  record.layers = { ...(record.layers || {}), linguistic: { ...result, lastCheckedAt: new Date().toISOString() } };
  record.updatedAt = new Date().toISOString();
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return record;
};

DataService.writeAddressIntelResult = async function writeAddressIntelResult(tenantId, carrierId, result) {
  if (!tenantId) throw new Error('DataService.writeAddressIntelResult: tenantId required');
  if (!carrierId) throw new Error('DataService.writeAddressIntelResult: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  _emitWriteTelemetry('carrier_vetting_signals', 'update', tid);

  const patch = {
    tenantId: tid,
    carrierId,
    'layers.address': { ...result, lastCheckedAt: serverTimestamp() },
    updatedAt: serverTimestamp(),
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    await setDoc(ref, patch, { merge: true });
    return { id: docId, ...patch };
  }

  const key = 'wc_carrier_vetting_signals';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = all.findIndex(d => d.id === docId);
  const record = idx >= 0 ? { ...all[idx] } : { id: docId, tenantId: tid, carrierId, layers: {} };
  record.layers = { ...(record.layers || {}), address: { ...result, lastCheckedAt: new Date().toISOString() } };
  record.updatedAt = new Date().toISOString();
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return record;
};

DataService.appendFraudFrictionLog = async function appendFraudFrictionLog(tenantId, carrierId, loadId, tier, reason) {
  if (!tenantId) throw new Error('DataService.appendFraudFrictionLog: tenantId required');
  if (!carrierId) throw new Error('DataService.appendFraudFrictionLog: carrierId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${carrierId}`;

  _emitWriteTelemetry('carrier_vetting_signals', 'update', tid);

  const entry = {
    loadId,
    tier,
    dispatcherReason: reason || null,
    timestamp: serverTimestamp(),
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_vetting_signals', docId);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? (snap.data().frictionLog || []) : [];
    await setDoc(ref, {
      tenantId: tid,
      carrierId,
      frictionLog: [...existing, entry],
      updatedAt: serverTimestamp(),
    }, { merge: true });
    return entry;
  }

  const key = 'wc_carrier_vetting_signals';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = all.findIndex(d => d.id === docId);
  const record = idx >= 0 ? { ...all[idx] } : { id: docId, tenantId: tid, carrierId, frictionLog: [] };
  record.frictionLog = [...(record.frictionLog || []), { ...entry, timestamp: new Date().toISOString() }];
  record.updatedAt = new Date().toISOString();
  if (idx >= 0) all[idx] = record;
  else all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return entry;
};

DataService.getCarrierCommHistory = async function getCarrierCommHistory(tenantId, carrierId) {
  if (!tenantId) throw new Error('DataService.getCarrierCommHistory: tenantId required');
  if (!carrierId) throw new Error('DataService.getCarrierCommHistory: carrierId required');
  const tid = normalizeTenantId(tenantId);

  if (isConfigured && db) {
    const q = query(
      collection(db, 'load_communications'),
      where('tenantId', '==', tid),
      where('carrierId', '==', carrierId),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  const all = JSON.parse(localStorage.getItem('wc_load_communications') || '[]');
  return all.filter(d =>
    normalizeTenantId(d.tenantId) === tid && d.carrierId === carrierId,
  );
};

DataService.logBankAccountChangeRequest = async function logBankAccountChangeRequest(tenantId, carrierId, delta) {
  if (!tenantId) throw new Error('DataService.logBankAccountChangeRequest: tenantId required');
  if (!carrierId) throw new Error('DataService.logBankAccountChangeRequest: carrierId required');
  const tid = normalizeTenantId(tenantId);

  _emitWriteTelemetry('carrier_bank_account_changes', 'create', tid);

  const payload = createDocument({
    ...delta,
    tenantId: tid,
    carrierId,
    status: 'pending_verification',
  }, delta.requestedBy || 'system');

  if (isConfigured && db) {
    const ref = await addDoc(collection(db, 'carrier_bank_account_changes'), payload);
    return { id: ref.id, ...payload };
  }

  const key = 'wc_carrier_bank_account_changes';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const record = { id: `change_${Date.now()}`, ...payload, createdAt: new Date().toISOString() };
  all.push(record);
  localStorage.setItem(key, JSON.stringify(all));
  return record;
};

DataService.markBankAccountChangeVerified = async function markBankAccountChangeVerified(tenantId, carrierId, verificationId) {
  if (!tenantId) throw new Error('DataService.markBankAccountChangeVerified: tenantId required');
  if (!carrierId) throw new Error('DataService.markBankAccountChangeVerified: carrierId required');
  const tid = normalizeTenantId(tenantId);

  _emitWriteTelemetry('carrier_bank_account_changes', 'update', tid);

  const now = serverTimestamp();
  const patch = {
    status: 'verified',
    verifiedAt: now,
    verifiedBy: 'aegis_voice',
    updatedAt: now,
  };

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_bank_account_changes', verificationId);
    await updateDoc(ref, patch);

    // Also flip the payment layer on carrier_vetting_signals
    const sigRef = doc(db, 'carrier_vetting_signals', `${tid}_${carrierId}`);
    await setDoc(sigRef, {
      tenantId: tid,
      carrierId,
      'layers.payment': { bankAccountChangeVerified: true, lastChangeVerifiedAt: now },
      updatedAt: now,
    }, { merge: true });

    return { id: verificationId, ...patch };
  }

  const key = 'wc_carrier_bank_account_changes';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = all.findIndex(d => d.id === verificationId);
  if (idx >= 0) {
    all[idx] = { ...all[idx], ...patch, verifiedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    localStorage.setItem(key, JSON.stringify(all));
  }
  return { id: verificationId, ...patch };
};

// ── END CARRIER-VETTING-WORLD-CLASS-1 ─────────────────────────────────────

export default DataService;

const _realtimeLogger = createLogger('subscribeRealtime');

// Lazy imports for Ably transport — only loaded when a hot streamId is requested.
// Keeps the Firestore-only path free of Ably overhead.
let _ablyTransport = null;
async function _getAblyTransport() {
  if (!_ablyTransport) {
    _ablyTransport = await import('./realtime/AblyTransport.js');
  }
  return _ablyTransport;
}

/**
 * subscribeRealtime — canonical realtime subscription facade (REALTIME-1).
 *
 * Dual-mode: hot streams route via Ably (sub-200ms p95), cold streams via Firestore.
 * Existing call-site API is fully preserved — `streamId` is the only new optional param.
 *
 * Hot-stream routing:
 *   • Pass `streamId` matching a registered hot stream → Ably transport
 *   • On Ably failure → auto-downgrade to Firestore + onDowngrade callback
 *   • Without `streamId` or for non-hot streams → Firestore (unchanged behaviour)
 *
 * Channel naming (BINDING): tenants:{tenantId}:streams:{streamId}
 *   (SEAT-ABLY-CHANNEL-NAMESPACE-1, 2026-05-22 — `:` is Ably's segment delimiter.)
 *
 * @param {Object} params
 * @param {string} params.collection - Firestore collection name (used for Firestore path + offline fallback)
 * @param {string} params.tenantId   - Required; rejected if absent
 * @param {string} [params.streamId] - Hot stream ID (e.g. 'ncl-sailing-command-center')
 * @param {Array<[string, string, any]>} [params.where] - Additional where clauses (Firestore path only)
 * @param {Array<[string, 'asc'|'desc']>} [params.orderBy]
 * @param {number} [params.limit]
 * @param {boolean} [params.includeSoftDeleted=false]
 * @param {(items: Array, meta: {correlationId: string, source: 'cache'|'server'|'ably'}) => void} params.onNext
 * @param {(error: Error) => void} [params.onError]
 * @param {(reason: string) => void} [params.onDowngrade] - Called when Ably downgrades to Firestore
 * @returns {() => void} unsubscribe function
 */
export function subscribeRealtime({
  collection: collectionName,
  tenantId,
  streamId,
  where: extraWhere = [],
  orderBy: orderByArgs = [],
  limit: limitArg,
  includeSoftDeleted = false,
  onNext,
  onError,
  onDowngrade,
}) {
  if (!tenantId) throw new Error('subscribeRealtime: tenantId required');
  if (typeof onNext !== 'function') throw new Error('subscribeRealtime: onNext required');

  const correlationId = generateCorrelationId();

  // ── Offline / unconfigured fallback ──────────────────────────────────────
  if (!isConfigured || !db) {
    const items = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]')
      .filter(d => d.tenantId === tenantId && (includeSoftDeleted || !d.softDeleted));
    onNext(items, { correlationId, source: 'cache' });
    return () => {};
  }

  // ── Hot stream: dual Firestore+Ably subscription (SEAT-DATA-SEED-1, 2026-05-22) ──────
  // Firestore provides immediate initial state (onSnapshot fires even on empty
  // collections), ensuring isLoading always clears on first render.
  // Ably provides sub-second live updates; its onNext calls replace the Firestore
  // snapshot when messages arrive.
  // Previously: Ably-only path left isLoading stuck true until the first Ably
  // message arrived on a channel that had never received a published message.
  if (streamId) {
    let ablyUnsub = null;
    let firestoreUnsub = null;

    _getAblyTransport().then(({ isHotStream, subscribeAbly, onConnectionStateChange }) => {
      if (!isHotStream(streamId)) {
        // Not a registered hot stream — Firestore cold path only.
        firestoreUnsub = _subscribeFirestore({ collectionName, tenantId, extraWhere, orderByArgs, limitArg, includeSoftDeleted, onNext, onError, correlationId });
        return;
      }

      // Firestore leg — always active; guarantees initial state delivery.
      firestoreUnsub = _subscribeFirestore({
        collectionName, tenantId, extraWhere, orderByArgs, limitArg, includeSoftDeleted,
        onNext, onError, correlationId,
      });

      // Monitor for complete Ably connection failure → stale banner only
      // (Firestore leg continues regardless).
      const unlisten = onConnectionStateChange(tenantId, (state) => {
        if (state === 'failed' || state === 'suspended') {
          _realtimeLogger.warn('subscribeRealtime: Ably degraded, Firestore continues', { streamId, tenantId, state });
          if (onDowngrade) onDowngrade(state);
        }
      });

      subscribeAbly({ tenantId, streamId, onNext, onError, correlationId })
        .then((unsub) => {
          ablyUnsub = () => { unsub(); unlisten(); };
        })
        .catch((err) => {
          _realtimeLogger.error('subscribeRealtime: Ably subscribe failed', { streamId, tenantId, error: err?.message });
          // Firestore already running — signal downgrade for stale banner only.
          if (onDowngrade) onDowngrade('ably-subscribe-error');
        });
    }).catch(() => {
      // Ably transport import failed — Firestore only.
      if (!firestoreUnsub) {
        firestoreUnsub = _subscribeFirestore({ collectionName, tenantId, extraWhere, orderByArgs, limitArg, includeSoftDeleted, onNext, onError, correlationId });
      }
    });

    return () => {
      if (ablyUnsub) ablyUnsub();
      if (firestoreUnsub) firestoreUnsub();
    };
  }

  // ── Cold stream: Firestore (unchanged) ────────────────────────────────────
  return _subscribeFirestore({ collectionName, tenantId, extraWhere, orderByArgs, limitArg, includeSoftDeleted, onNext, onError, correlationId });
}

/**
 * Internal Firestore subscription — extracted so both the cold path and hot-stream
 * downgrade path share the same implementation.
 */
function _subscribeFirestore({ collectionName, tenantId, extraWhere, orderByArgs, limitArg, includeSoftDeleted, onNext, onError, correlationId }) {
  const constraints = [
    where('tenantId', '==', tenantId),
    ...extraWhere.map(([f, op, v]) => where(f, op, v)),
  ];
  if (!includeSoftDeleted) {
    constraints.push(where('softDeleted', '==', false));
  }
  orderByArgs.forEach(([field, dir]) => constraints.push(orderBy(field, dir)));
  if (limitArg) constraints.push(limit(limitArg));

  const q = query(collection(db, collectionName), ...constraints);

  return onSnapshot(
    q,
    { includeMetadataChanges: false },
    (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      onNext(items, {
        correlationId,
        source: snapshot.metadata.fromCache ? 'cache' : 'server',
      });
    },
    (error) => {
      _realtimeLogger.error('subscribeRealtime error', {
        collection: collectionName,
        tenantId,
        correlationId,
        error: error.message,
      });
      if (onError) onError(error);
    },
  );
}

/**
 * subscribeDoc — single-document realtime subscription.
 * Use this instead of importing onSnapshot directly for document-level listeners.
 *
 * @param {string} collectionName
 * @param {string} docId
 * @param {(data: object|null) => void} onNext
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe function
 */
export function subscribeDoc(collectionName, docId, onNext, onError) {
  if (!isConfigured || !db) {
    const all = JSON.parse(localStorage.getItem('wc_' + collectionName) || '[]');
    const item = all.find(d => d.id === docId && !d.isDeleted) || null;
    onNext(item);
    return () => {};
  }
  const ref = doc(db, collectionName, docId);
  return onSnapshot(
    ref,
    (snap) => onNext(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    (error) => {
      _realtimeLogger.error('subscribeDoc error', { collectionName, docId, error: error.message });
      if (onError) onError(error);
    },
  );
}

/**
 * subscribeRawQuery — escape hatch for complex pre-built Firestore queries.
 * The caller provides a Query object (built via firebase/firestore imports they
 * own); this routes the onSnapshot through DataService so the call stays inside
 * the adapter. Use only when subscribeRealtime's structured API can't express
 * the query (e.g., complex multi-clause conversation filters).
 *
 * @param {import('firebase/firestore').Query} q
 * @param {(items: Array) => void} onNext
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe function
 */
export function subscribeRawQuery(q, onNext, onError) {
  if (!isConfigured || !db) {
    onNext([]);
    return () => {};
  }
  return onSnapshot(
    q,
    (snap) => onNext(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (error) => {
      _realtimeLogger.error('subscribeRawQuery error', { error: error.message });
      if (onError) onError(error);
    },
  );
}

// ── Named exports for internal service consumers ──────────────────────────────
// Registry services (AGENT-REGISTRY-1, etc.) import these instead of using
// the default DataService object directly, maintaining encapsulation.

export async function insertDocument(col, data) {
  return DataService.create(col, data);
}

export async function patchDocument(col, id, patch) {
  return DataService.update(col, id, patch);
}

export async function queryDocuments(col, conditions = []) {
  const mapped = conditions.map(c =>
    Array.isArray(c) ? { field: c[0], op: c[1], value: c[2] } : c
  );
  return DataService.query(col, mapped);
}

// ── PREDICTIVE-ALERT-ENGINE-1 domain methods ──────────────────────────────────
// All five methods are tenant-scoped. No cross-tenant access possible.

/**
 * createLoadPredictionState — creates the initial prediction-state document
 * for an in-transit load. Uses the loadId as the Firestore document ID so
 * that lookups are O(1) by loadId without a secondary index.
 *
 * @param {string} tenantId
 * @param {string} loadId
 * @param {object} data  — initial prediction state payload (sans tenantId/loadId)
 * @returns {Promise<object>} created document
 */
export async function createLoadPredictionState(tenantId, loadId, data) {
  if (!tenantId) throw new Error('createLoadPredictionState: tenantId required');
  if (!loadId) throw new Error('createLoadPredictionState: loadId required');
  const tid = normalizeTenantId(tenantId);

  const payload = {
    ...data,
    tenantId: tid,
    loadId,
  };

  _emitWriteTelemetry('load_prediction_state', 'create', tid);

  if (isConfigured && db) {
    const ref = doc(db, 'load_prediction_state', `${tid}_${loadId}`);
    const document = createDocument(payload, 'system', 'prediction-engine');
    await setDoc(ref, document);
    return { id: ref.id, ...document };
  }

  // localStorage fallback
  const key = 'wc_load_prediction_state';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const document = createDocument(payload, 'system', 'prediction-engine');
  document.id = `${tid}_${loadId}`;
  const idx = existing.findIndex(d => d.id === document.id);
  if (idx >= 0) existing[idx] = document;
  else existing.push(document);
  localStorage.setItem(key, JSON.stringify(existing));
  return document;
}

/**
 * updateLoadPredictionState — merges delta into an existing prediction-state doc.
 * Appends a predictionLog entry when delta contains scoredAt + probabilityScore.
 *
 * @param {string} tenantId
 * @param {string} loadId
 * @param {object} delta — partial update; may include a logEntry to append
 * @returns {Promise<object>}
 */
export async function updateLoadPredictionState(tenantId, loadId, delta) {
  if (!tenantId) throw new Error('updateLoadPredictionState: tenantId required');
  if (!loadId) throw new Error('updateLoadPredictionState: loadId required');
  const tid = normalizeTenantId(tenantId);

  const docId = `${tid}_${loadId}`;
  const updates = updateDocument(delta, 'system');
  _emitWriteTelemetry('load_prediction_state', 'update', tid);

  if (isConfigured && db) {
    const ref = doc(db, 'load_prediction_state', docId);
    await updateDoc(ref, { ...updates, version: increment(1) });
    return { id: docId, ...updates };
  }

  const key = 'wc_load_prediction_state';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = existing.findIndex(d => d.id === docId);
  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...updates, version: (existing[idx].version || 0) + 1 };
    localStorage.setItem(key, JSON.stringify(existing));
  }
  return { id: docId, ...updates };
}

/**
 * getCarrierBehaviorProfile — fetches the OTP profile for a carrier on a lane
 * on a given weekday. Returns null when no profile exists yet.
 *
 * @param {string} tenantId
 * @param {string} carrierId
 * @param {string} laneKey  — `${originState}_${destState}`
 * @param {number} weekday  — 0 (Sun) … 6 (Sat)
 * @returns {Promise<object|null>}
 */
export async function getCarrierBehaviorProfile(tenantId, carrierId, laneKey, weekday) {
  if (!tenantId) throw new Error('getCarrierBehaviorProfile: tenantId required');
  const tid = normalizeTenantId(tenantId);

  const docId = `${tid}_${carrierId}_${laneKey}_${weekday}`;

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_behavior_profile', docId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    const data = snap.data();
    // Tenant isolation guard
    if (normalizeTenantId(data.tenantId) !== tid) return null;
    return { id: snap.id, ...data };
  }

  const key = 'wc_carrier_behavior_profile';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const found = existing.find(d => d.id === docId && normalizeTenantId(d.tenantId) === tid);
  return found || null;
}

/**
 * appendBehaviorProfileEntry — upserts a carrier_behavior_profile doc,
 * appends one OTP history entry, and recomputes otpRate + avgLateMinutes
 * over the rolling 90-day window.
 *
 * @param {string} tenantId
 * @param {string} carrierId
 * @param {string} laneKey
 * @param {object} entry  — { loadId, scheduledDelivery, actualDelivery, onTime, minutesVariance }
 * @returns {Promise<object>} updated profile
 */
export async function appendBehaviorProfileEntry(tenantId, carrierId, laneKey, entry) {
  if (!tenantId) throw new Error('appendBehaviorProfileEntry: tenantId required');
  if (!carrierId || !laneKey) throw new Error('appendBehaviorProfileEntry: carrierId and laneKey required');
  const tid = normalizeTenantId(tenantId);

  // Parse weekday from scheduled delivery
  const deliveryDate = new Date(entry.scheduledDelivery);
  const weekday = deliveryDate.getDay();
  const docId = `${tid}_${carrierId}_${laneKey}_${weekday}`;

  // Rolling 90-day cutoff
  const cutoff90d = new Date();
  cutoff90d.setDate(cutoff90d.getDate() - 90);

  _emitWriteTelemetry('carrier_behavior_profile', 'update', tid);

  if (isConfigured && db) {
    const ref = doc(db, 'carrier_behavior_profile', docId);
    const snap = await getDoc(ref);
    const existing = snap.exists() ? snap.data() : {
      tenantId: tid, carrierId, laneKey, weekday,
      otpHistory: [], otpRate: 0, avgLateMinutes: 0, sampleSize: 0,
    };

    // Tenant guard
    if (snap.exists() && normalizeTenantId(existing.tenantId) !== tid) {
      throw new Error('appendBehaviorProfileEntry: tenant isolation violation');
    }

    const history = [...(existing.otpHistory || []), entry]
      .filter(h => new Date(h.scheduledDelivery) >= cutoff90d);

    const { otpRate, avgLateMinutes } = _computeOtpStats(history);

    const updated = {
      ...existing,
      tenantId: tid,
      carrierId,
      laneKey,
      weekday,
      otpHistory: history,
      otpRate,
      avgLateMinutes,
      sampleSize: history.length,
      updatedAt: serverTimestamp(),
    };

    await setDoc(ref, updated, { merge: true });
    return { id: docId, ...updated };
  }

  // localStorage fallback
  const storageKey = 'wc_carrier_behavior_profile';
  const all = JSON.parse(localStorage.getItem(storageKey) || '[]');
  const idx = all.findIndex(d => d.id === docId);
  const existing = idx >= 0 ? all[idx] : {
    id: docId, tenantId: tid, carrierId, laneKey, weekday,
    otpHistory: [], otpRate: 0, avgLateMinutes: 0, sampleSize: 0,
  };

  const history = [...(existing.otpHistory || []), entry]
    .filter(h => new Date(h.scheduledDelivery) >= cutoff90d);

  const { otpRate, avgLateMinutes } = _computeOtpStats(history);

  const updated = {
    ...existing,
    otpHistory: history,
    otpRate,
    avgLateMinutes,
    sampleSize: history.length,
    updatedAt: new Date().toISOString(),
  };

  if (idx >= 0) all[idx] = updated;
  else all.push(updated);
  localStorage.setItem(storageKey, JSON.stringify(all));
  return updated;
}

/**
 * getActiveInTransitLoads — returns all non-deleted loads with
 * status='in_transit' for a given tenant. Used by runPredictionCycle CF
 * to iterate over all loads requiring a scoring pass.
 *
 * @param {string} tenantId
 * @returns {Promise<Array>}
 */
export async function getActiveInTransitLoads(tenantId) {
  if (!tenantId) throw new Error('getActiveInTransitLoads: tenantId required');
  const tid = normalizeTenantId(tenantId);

  const _t0 = performance.now();

  if (isConfigured && db) {
    const q = query(
      collection(db, 'loads'),
      where('tenantId', '==', tid),
      where('status', '==', 'in_transit'),
      where('isDeleted', '==', false),
      orderBy('createdAt', 'desc'),
    );
    const snap = await getDocs(q);
    _recordQueryPerf('query', _t0, 'loads');
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }

  // localStorage fallback
  const all = JSON.parse(localStorage.getItem('wc_loads') || '[]');
  return all.filter(d =>
    normalizeTenantId(d.tenantId) === tid &&
    d.status === 'in_transit' &&
    !d.isDeleted,
  );
}

// ── Internal helpers for PREDICTIVE-ALERT-ENGINE-1 ───────────────────────────

/**
 * Recomputes OTP rate and average late minutes from a history array.
 * Entries where onTime=true contribute 0 variance to avgLateMinutes.
 *
 * @param {Array} history
 * @returns {{ otpRate: number, avgLateMinutes: number }}
 */
function _computeOtpStats(history) {
  if (!history.length) return { otpRate: 0, avgLateMinutes: 0 };
  const onTimeCount = history.filter(h => h.onTime).length;
  const lateEntries = history.filter(h => !h.onTime);
  const otpRate = onTimeCount / history.length;
  const avgLateMinutes = lateEntries.length
    ? lateEntries.reduce((sum, h) => sum + (h.minutesVariance ?? 0), 0) / lateEntries.length
    : 0;
  return { otpRate, avgLateMinutes };
}

// ── TRANSIT-COMMS-INTELLIGENCE-1 ─────────────────────────────────────────────

/**
 * createConsigneeThread — creates a new comms_threads doc for the consignee
 * party on a load. Called at load dispatch. KCOL-THREAD-HANDOFF-1 stub path.
 */
export async function createConsigneeThread(tenantId, loadId, consigneeContact) {
  if (!tenantId) throw new Error('createConsigneeThread: tenantId required');
  if (!loadId) throw new Error('createConsigneeThread: loadId required');
  const tid = normalizeTenantId(tenantId);

  const payload = {
    tenantId: tid,
    loadId,
    partyType: 'consignee',
    partyName: consigneeContact?.name || '',
    partyPhone: consigneeContact?.phone || null,
    partyEmail: consigneeContact?.email || null,
    events: [],
  };

  _emitWriteTelemetry('comms_threads', 'create', tid);

  if (isConfigured && db) {
    const document = createDocument(payload, 'system', 'transit-comms');
    const ref = await addDoc(collection(db, 'comms_threads'), document);
    return { id: ref.id, ...document };
  }

  const key = 'wc_comms_threads';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const document = createDocument(payload, 'system', 'transit-comms');
  document.id = `consignee_${tid}_${loadId}_${Date.now()}`;
  existing.push(document);
  localStorage.setItem(key, JSON.stringify(existing));
  return document;
}

/**
 * getLoadGeofenceState — fetches geofence ring state for a load.
 * Doc ID: `${tenantId}_${loadId}`
 */
export async function getLoadGeofenceState(tenantId, loadId) {
  if (!tenantId) throw new Error('getLoadGeofenceState: tenantId required');
  if (!loadId) throw new Error('getLoadGeofenceState: loadId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${loadId}`;

  if (isConfigured && db) {
    const ref = doc(db, 'load_geofence_state', docId);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }

  const all = JSON.parse(localStorage.getItem('wc_load_geofence_state') || '[]');
  return all.find(d => d.id === docId) || null;
}

/**
 * updateLoadGeofenceState — merges delta into the geofence state doc.
 * Creates the doc if it doesn't exist yet (upsert via setDoc merge).
 */
export async function updateLoadGeofenceState(tenantId, loadId, delta) {
  if (!tenantId) throw new Error('updateLoadGeofenceState: tenantId required');
  if (!loadId) throw new Error('updateLoadGeofenceState: loadId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${loadId}`;

  const updates = updateDocument(delta, 'system');
  _emitWriteTelemetry('load_geofence_state', 'update', tid);

  if (isConfigured && db) {
    const ref = doc(db, 'load_geofence_state', docId);
    await setDoc(ref, { ...updates, tenantId: tid, loadId }, { merge: true });
    return { id: docId, ...updates };
  }

  const key = 'wc_load_geofence_state';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const idx = existing.findIndex(d => d.id === docId);
  if (idx >= 0) {
    existing[idx] = { ...existing[idx], ...updates, version: (existing[idx].version || 0) + 1 };
  } else {
    existing.push({ id: docId, tenantId: tid, loadId, ...updates });
  }
  localStorage.setItem(key, JSON.stringify(existing));
  return { id: docId, ...updates };
}

/**
 * getLoadCadenceTier — fetches the comms cadence tier doc for a load.
 * Doc ID: `${tenantId}_${loadId}`
 */
export async function getLoadCadenceTier(tenantId, loadId) {
  if (!tenantId) throw new Error('getLoadCadenceTier: tenantId required');
  if (!loadId) throw new Error('getLoadCadenceTier: loadId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${loadId}`;

  if (isConfigured && db) {
    const ref = doc(db, 'load_comms_cadence', docId);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  }

  const all = JSON.parse(localStorage.getItem('wc_load_comms_cadence') || '[]');
  return all.find(d => d.id === docId) || null;
}

/**
 * setLoadCadenceTier — creates or replaces the cadence tier doc for a load.
 */
export async function setLoadCadenceTier(tenantId, loadId, tier, tierSetBy = 'auto', rationale = null) {
  if (!tenantId) throw new Error('setLoadCadenceTier: tenantId required');
  if (!loadId) throw new Error('setLoadCadenceTier: loadId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${loadId}`;

  const payload = {
    tenantId: tid,
    loadId,
    tier,
    tierSetBy,
    ...(rationale ? { rationale } : {}),
    rescheduleOfferSent: false,
    rescheduleOfferAccepted: null,
    rescheduleCompletedAt: null,
  };

  _emitWriteTelemetry('load_comms_cadence', 'create', tid);

  if (isConfigured && db) {
    const ref = doc(db, 'load_comms_cadence', docId);
    const document = createDocument(payload, 'system', 'transit-comms');
    await setDoc(ref, document);
    return { id: docId, ...document };
  }

  const key = 'wc_load_comms_cadence';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  const document = createDocument(payload, 'system', 'transit-comms');
  document.id = docId;
  const idx = existing.findIndex(d => d.id === docId);
  if (idx >= 0) existing[idx] = document;
  else existing.push(document);
  localStorage.setItem(key, JSON.stringify(existing));
  return document;
}

/**
 * appendCommsEvent — appends a communication event to the matching
 * comms_threads doc (found by loadId + partyType). Creates a stub thread
 * if none exists yet for that partyType.
 *
 * @param {string} tenantId
 * @param {string} loadId
 * @param {object} event  — shape: { partyType, direction, channel, body,
 *                          bodyParsed?, sentAt?, agentGenerated?, ... }
 */
export async function appendCommsEvent(tenantId, loadId, event) {
  if (!tenantId) throw new Error('appendCommsEvent: tenantId required');
  if (!loadId) throw new Error('appendCommsEvent: loadId required');
  if (!event?.partyType) throw new Error('appendCommsEvent: event.partyType required');
  const tid = normalizeTenantId(tenantId);

  const { partyType, partyName, partyPhone, partyEmail, ...eventFields } = event;
  const eventRecord = {
    id: eventFields.id || `ev_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    direction:      eventFields.direction,
    channel:        eventFields.channel,
    body:           eventFields.body || '',
    bodyParsed:     eventFields.bodyParsed || null,
    sentAt:         eventFields.sentAt || new Date().toISOString(),
    deliveredAt:    eventFields.deliveredAt || null,
    agentGenerated: eventFields.agentGenerated ?? false,
  };

  _emitWriteTelemetry('comms_threads', 'update', tid);

  if (isConfigured && db) {
    const q = query(
      collection(db, 'comms_threads'),
      where('tenantId', '==', tid),
      where('loadId', '==', loadId),
      where('partyType', '==', partyType),
      where('isDeleted', '==', false),
      limit(1),
    );
    const snap = await getDocs(q);

    if (!snap.empty) {
      const threadRef = doc(db, 'comms_threads', snap.docs[0].id);
      await updateDoc(threadRef, {
        events: arrayUnion(eventRecord),
        updatedAt: serverTimestamp(),
        version: increment(1),
      });
      return { threadId: snap.docs[0].id, event: eventRecord };
    }

    // No thread found — create stub thread with this first event
    const newThread = createDocument({
      tenantId: tid, loadId, partyType,
      partyName: partyName || partyType,
      partyPhone: partyPhone || null,
      partyEmail: partyEmail || null,
      events: [eventRecord],
    }, 'system', 'transit-comms');
    const newRef = await addDoc(collection(db, 'comms_threads'), newThread);
    return { threadId: newRef.id, event: eventRecord };
  }

  // localStorage fallback
  const key = 'wc_comms_threads';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const thread = all.find(d =>
    normalizeTenantId(d.tenantId) === tid &&
    d.loadId === loadId &&
    d.partyType === partyType &&
    !d.isDeleted,
  );
  if (thread) {
    thread.events = [...(thread.events || []), eventRecord];
    thread.version = (thread.version || 0) + 1;
    localStorage.setItem(key, JSON.stringify(all));
    return { threadId: thread.id, event: eventRecord };
  }
  const stub = createDocument({
    tenantId: tid, loadId, partyType,
    partyName: partyName || partyType,
    partyPhone: partyPhone || null,
    partyEmail: partyEmail || null,
    events: [eventRecord],
  }, 'system', 'transit-comms');
  stub.id = `thread_${partyType}_${tid}_${loadId}`;
  all.push(stub);
  localStorage.setItem(key, JSON.stringify(all));
  return { threadId: stub.id, event: eventRecord };
}

/**
 * patchLoadCadenceTier — partial update on a load_comms_cadence doc.
 * Used for updating rescheduleOfferSent, rescheduleOfferAccepted, etc.
 */
export async function patchLoadCadenceTier(tenantId, loadId, patch) {
  if (!tenantId) throw new Error('patchLoadCadenceTier: tenantId required');
  if (!loadId)   throw new Error('patchLoadCadenceTier: loadId required');
  const tid = normalizeTenantId(tenantId);
  const docId = `${tid}_${loadId}`;
  _emitWriteTelemetry('load_comms_cadence', 'update', tid);
  if (isConfigured && db) {
    const ref = doc(db, 'load_comms_cadence', docId);
    await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
    return { id: docId };
  }
  const key = 'wc_load_comms_cadence';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const item = all.find(d => d.id === docId);
  if (item) Object.assign(item, patch);
  localStorage.setItem(key, JSON.stringify(all));
  return { id: docId };
}

/**
 * getConsigneeThread — fetches the consignee comms_thread for a load.
 * Returns null if no consignee thread exists.
 */
export async function getConsigneeThread(tenantId, loadId) {
  if (!tenantId) throw new Error('getConsigneeThread: tenantId required');
  if (!loadId)   throw new Error('getConsigneeThread: loadId required');
  const tid = normalizeTenantId(tenantId);
  if (isConfigured && db) {
    const q = query(
      collection(db, 'comms_threads'),
      where('tenantId', '==', tid),
      where('loadId', '==', loadId),
      where('partyType', '==', 'consignee'),
      where('isDeleted', '==', false),
      limit(1),
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return { id: snap.docs[0].id, ...snap.docs[0].data() };
  }
  const key = 'wc_comms_threads';
  const all = JSON.parse(localStorage.getItem(key) || '[]');
  const thread = all.find(d =>
    normalizeTenantId(d.tenantId) === tid &&
    d.loadId === loadId &&
    d.partyType === 'consignee' &&
    !d.isDeleted,
  );
  return thread || null;
}
